import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Shield, Eye, Lock, FileText, Download, ExternalLink,
  ChevronRight, Users, Star, AlertTriangle, X,
  Phone, Send, Mail, CheckCircle, Clock, Search,
  Skull, Zap, Crown, BookOpen, Play, Target, LockOpen, Cpu, Wifi,
} from "lucide-react";
const logoImg = "/assets/new-logo.png";
const maskLogoImg = "/assets/39e6e2ed2506a2f9985c0fd89805b5ecd3b397ec.png";
const compassImg = "/assets/563c65b9036208368eb77508945cc4e14986d818.png";
const masterclassImg = "/assets/42bcfb8bc3f34e221c8cd72cefa824988d41032c.png";
const errataThumbnail = "/assets/9c5a0c38b4466f208e9976e24b8c29afd4324082.png";
const montaThumb = "/assets/d387c3e57729c3ee43dace0096765ad8a3598ebb.png";
const toxicThumb = "/assets/683a09c2e56dff49530c31f69d8dba51ec8279cf.png";
const finanzasImg = "/assets/1dff30e171396769d54b7cc43f9b4508ab4b3c00.png";
const zonaSeguraBg = "/assets/2f74992506fbea7b7c869e4b9e0d64f25d53c19f.png";
const bypassThumb = "/assets/ca80017a39d3bc13825c57fe2a2c06502b1ea094.png";

import { MysticalBg, MP } from "./MysticalBg";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { BotLogo } from "./BotLogo";
import { DT } from "./DeltaTypography";
import { BackButton } from "./BackButton";

// ─── PALETA ──────────────────────────────────────────────────────────────────
const P = {
  blood:        MP.crimson,
  gold:         MP.goldBright,
  goldDim:      MP.gold,
  parchment:    MP.parchment,
  parchmentDim: MP.parchmentDim,
  abyss:        MP.abyss,
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function Divider({ label }) {
  return (
    <div className="flex items-center gap-4 w-full my-2">
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, rgba(107,0,21,0.55))` }} />
      <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "15px", color: `rgba(255,160,40,0.75)`, letterSpacing: "0.35em", whiteSpace: "nowrap" }}>
        ── {label} ──
      </span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, rgba(107,0,21,0.55), transparent)` }} />
    </div>
  );
}

function PulsingDot({ color, speed = 2 }) {
  return (
    <motion.div
      style={{ width: 5, height: 5, borderRadius: "50%", background: color, flexShrink: 0 }}
      animate={{ opacity: [1, 0.2, 1], scale: [1, 1.4, 1] }}
      transition={{ duration: speed, repeat: Infinity, ease: "easeInOut" }}
    />
  );
}

function SectionTitle({ greek, spanish }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="flex flex-col items-center gap-1 text-center"
    >
      <motion.h2
        animate={{ textShadow: [`0 0 20px rgba(107,0,21,0.5), 0 0 40px rgba(80,0,10,0.2)`, `0 0 40px rgba(107,0,21,0.85), 0 0 80px rgba(60,0,8,0.4)`, `0 0 20px rgba(107,0,21,0.5), 0 0 40px rgba(80,0,10,0.2)`] }}
        transition={{ duration: 5, repeat: Infinity }}
        style={{ fontFamily: DT.greek, fontSize: "17px", color: P.parchment, letterSpacing: "0.12em" }}
      >{greek}</motion.h2>
      <p style={{ fontFamily: DT.data, fontSize: "15px", color: `rgba(255,160,40,0.82)`, letterSpacing: "0.28em",
        textShadow: "0 0 12px rgba(255,160,40,0.45), 0 0 28px rgba(200,120,20,0.22)" }}>
        {spanish}
      </p>
      <div style={{ width: "clamp(240px, 50%, 600px)", height: 1, marginTop: 6, background: `linear-gradient(90deg, transparent, rgba(107,0,21,0.6), rgba(200,120,20,0.5), rgba(107,0,21,0.6), transparent)` }} />
    </motion.div>
  );
}

// ─── BLOQUE 1 · ZONA SEGURA ───────────────────────────────────────────────────
const WEAPON_STATS = [
  { value: "1,847", label: "OBJETIVOS RASTREADOS",       color: "rgba(255,160,40,0.9)" },
  { value: "100%",  label: "CASOS RESUELTOS",            color: "rgba(255,160,40,0.9)" },
  { value: "48H",   label: "TIEMPO MÁX. DE RESPUESTA",  color: MP.crimsonBright },
  { value: "∞",     label: "ALCANCE DE VIGILANCIA",     color: "rgba(255,160,40,0.85)" },
];

function AnimatedCounter({ target }) {
  const [display, setDisplay] = useState("000");
  useEffect(() => {
    const chars = "0123456789";
    let frame = 0;
    const total = 28;
    const iv = setInterval(() => {
      if (frame >= total) { setDisplay(target); clearInterval(iv); return; }
      setDisplay(
        target.split("").map((c) =>
          /\d/.test(c) ? chars[Math.floor(Math.random() * 10)] : c
        ).join("")
      );
      frame++;
    }, 55);
    return () => clearInterval(iv);
  }, [target]);
  return <>{display}</>;
}

function ZonaSegura() {
  const navigate = useNavigate();
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="relative overflow-hidden rounded-sm flex flex-col items-center gap-0 text-center w-full"
      style={{
        border: `1px solid rgba(107,0,21,0.6)`,
        boxShadow: `0 0 80px rgba(70,0,10,0.55), inset 0 0 60px rgba(30,0,6,0.35)`,
      }}
    >
      {/* ── Fondo: imagen épica + capas de oscurecimiento ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Imagen de fondo */}
        <img
          src={zonaSeguraBg}
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 60%", opacity: 0.72 }}
        />
        {/* Oscuro arriba para que Ryuk se pierda, transparente en zona de monjes */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(3,1,7,0.75) 0%, rgba(3,1,7,0.15) 28%, transparent 50%, rgba(3,1,7,0.80) 88%, rgba(3,1,7,0.98) 100%)" }} />
        {/* Velo carmesí muy sutil arriba */}
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(107,0,21,0.18) 0%, transparent 45%)" }} />
        {/* Velo dorado pulsante */}
        <motion.div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 100%, rgba(200,120,20,0.18) 0%, rgba(200,120,20,0.05) 45%, transparent 65%)" }}
          animate={{ opacity: [0.5, 0.9, 0.5] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1.5 }} />
        {/* Línea barrido */}
        <motion.div
          style={{ position: "absolute", left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.7), rgba(255,160,40,0.35), rgba(107,0,21,0.7), transparent)", boxShadow: "0 0 14px rgba(107,0,21,0.6)" }}
          animate={{ top: ["0%", "100%"] }} transition={{ duration: 10, repeat: Infinity, ease: "linear", repeatDelay: 2 }} />
        {/* Marco interior pulsante */}
        <motion.div style={{ position: "absolute", inset: 6, border: "1px solid rgba(107,0,21,0.12)", borderRadius: 2 }}
          animate={{ opacity: [0.3, 0.8, 0.3], boxShadow: ["inset 0 0 20px rgba(107,0,21,0.05)", "inset 0 0 60px rgba(107,0,21,0.15)", "inset 0 0 20px rgba(107,0,21,0.05)"] }}
          transition={{ duration: 5.5, repeat: Infinity }} />
        {/* Partículas */}
        <motion.div style={{ position: "absolute", left: "7%", width: 2, height: 2, borderRadius: "50%", background: "rgba(255,160,40,0.65)", boxShadow: "0 0 6px rgba(255,160,40,0.85)" }}
          animate={{ top: ["20%","75%","35%","60%","20%"], opacity: [0, 1, 0.6, 1, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div style={{ position: "absolute", right: "11%", width: 2, height: 2, borderRadius: "50%", background: "rgba(107,0,21,0.9)", boxShadow: "0 0 8px rgba(107,0,21,1)" }}
          animate={{ top: ["70%","25%","55%","15%","70%"], opacity: [0, 1, 0.5, 1, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut", delay: 5 }} />
        <motion.div style={{ position: "absolute", left: "51%", width: 1.5, height: 1.5, borderRadius: "50%", background: "rgba(180,130,20,0.7)", boxShadow: "0 0 6px rgba(180,130,20,0.9)" }}
          animate={{ top: ["85%","10%","50%","30%","85%"], opacity: [0, 0.8, 0.4, 0.9, 0] }} transition={{ duration: 26, repeat: Infinity, ease: "easeInOut", delay: 10 }} />
      </div>

      {/* ── BARRA DE ESTADO SUPERIOR ── */}
      <div className="w-full relative z-10 flex items-center justify-between px-6 py-2"
        style={{ borderBottom: "1px solid rgba(107,0,21,0.3)", background: "rgba(107,0,21,0.07)" }}>
        <div className="flex items-center gap-2">
          <motion.div style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(80,220,80,0.9)", boxShadow: "0 0 8px rgba(80,220,80,0.8)" }}
            animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 1.4, repeat: Infinity }} />
          <span style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(80,220,80,1)", letterSpacing: "0.2em", textShadow: "0 0 12px rgba(80,220,80,0.95), 0 1px 2px rgba(0,0,0,0.9)" }}>SISTEMA ACTIVO</span>
        </div>
        <motion.span animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity }}
          style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(230,185,60,1)", letterSpacing: "0.18em", textShadow: "0 0 12px rgba(200,160,40,0.9), 0 1px 2px rgba(0,0,0,0.9)" }}>
          OMNISCIENT OBSERVER · EN LÍNEA
        </motion.span>
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(210,175,80,1)", letterSpacing: "0.15em", textShadow: "0 0 10px rgba(180,140,40,0.8), 0 1px 2px rgba(0,0,0,0.9)" }}>CIFRADO AES-256</span>
          <Lock size={8} color="rgba(210,175,80,1)" />
        </div>
      </div>

      {/* ── RADAR + STATS (lado a lado) ── */}
      <div className="w-full relative z-10 flex flex-col sm:flex-row items-stretch justify-center"
        style={{ borderBottom: "1px solid rgba(107,0,21,0.25)" }}>

        {/* Logo + Radar */}
        <div className="relative flex items-center justify-center flex-shrink-0 py-6 px-6" style={{ width: 248, height: 248 }}>

          {/* Anillo estático base — sanctum */}
          <motion.div
            style={{ position: "absolute", width: 130, height: 130, borderRadius: "50%", border: "1px solid rgba(107,0,21,0.5)" }}
            animate={{ boxShadow: ["0 0 20px rgba(107,0,21,0.3), inset 0 0 10px rgba(107,0,21,0.08)", "0 0 45px rgba(107,0,21,0.65), inset 0 0 30px rgba(107,0,21,0.18)", "0 0 20px rgba(107,0,21,0.3), inset 0 0 10px rgba(107,0,21,0.08)"] }}
            transition={{ duration: 3.5, repeat: Infinity }}
          />

          {/* Anillo dorado giratorio — corona de la logia */}
          <motion.div
            style={{ position: "absolute", width: 116, height: 116, borderRadius: "50%", border: "1px solid rgba(200,120,20,0.3)" }}
            animate={{ opacity: [0.3, 0.8, 0.3], rotate: [0, 360] }}
            transition={{ opacity: { duration: 3, repeat: Infinity }, rotate: { duration: 40, repeat: Infinity, ease: "linear" } }}
          />

          {/* Halo carmesí bajo el logo */}
          <motion.div
            style={{ position: "absolute", width: 106, height: 106, borderRadius: "50%", background: "radial-gradient(circle, rgba(107,0,21,0.28) 0%, rgba(107,0,21,0.08) 55%, transparent 75%)" }}
            animate={{ scale: [0.9, 1.1, 0.9], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Logo omnisciente */}
          <motion.img
            src={maskLogoImg}
            alt="Omniscient Observer"
            style={{ position: "relative", zIndex: 3, width: 92, height: 92, objectFit: "contain" }}
            animate={{
              filter: [
                "drop-shadow(0 0 8px rgba(107,0,21,0.55)) drop-shadow(0 0 18px rgba(107,0,21,0.25)) brightness(0.92)",
                "drop-shadow(0 0 22px rgba(107,0,21,0.95)) drop-shadow(0 0 45px rgba(180,0,30,0.5)) drop-shadow(0 0 70px rgba(107,0,21,0.3)) brightness(1.08)",
                "drop-shadow(0 0 8px rgba(107,0,21,0.55)) drop-shadow(0 0 18px rgba(107,0,21,0.25)) brightness(0.92)",
              ],
            }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Partícula orbital carmesí */}
          <motion.div
            style={{ position: "absolute", width: 4, height: 4, borderRadius: "50%", background: "rgba(107,0,21,0.9)", boxShadow: "0 0 8px rgba(107,0,21,1)", zIndex: 2 }}
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            transformTemplate={({ rotate }) => `rotate(${rotate}) translateX(65px)`}
          />

          {/* Partícula orbital dorada */}
          <motion.div
            style={{ position: "absolute", width: 3, height: 3, borderRadius: "50%", background: "rgba(255,160,40,0.85)", boxShadow: "0 0 6px rgba(255,160,40,0.9)", zIndex: 2 }}
            animate={{ rotate: [180, -180] }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            transformTemplate={({ rotate }) => `rotate(${rotate}) translateX(58px)`}
          />
        </div>

        {/* Separador vertical (solo en sm+) */}
        <motion.div
          className="hidden sm:block self-stretch flex-shrink-0"
          style={{ width: 1, background: "linear-gradient(180deg, transparent 0%, rgba(107,0,21,0.45) 25%, rgba(200,120,20,0.25) 50%, rgba(107,0,21,0.45) 75%, transparent 100%)" }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 4, repeat: Infinity }}
        />

        {/* Stats — grid 2×2 al lado del radar */}
        <div className="flex-1 grid grid-cols-2">
          {WEAPON_STATS.map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.15 * i, duration: 0.5 }}
              className="flex flex-col items-center justify-center gap-1 py-6 px-4"
              style={{
                borderRight: i % 2 === 0 ? "1px solid rgba(107,0,21,0.2)" : "none",
                borderBottom: i < 2 ? "1px solid rgba(107,0,21,0.18)" : "none",
                background: i % 2 === 0 ? "rgba(107,0,21,0.04)" : "transparent",
              }}
            >
              <motion.span
                animate={{ textShadow: [`0 0 6px ${s.color}`, `0 0 22px ${s.color}, 0 0 40px ${s.color}55`, `0 0 6px ${s.color}`], opacity: [0.85, 1, 0.85] }}
                transition={{ duration: 2.5 + i * 0.4, repeat: Infinity, delay: i * 0.5 }}
                style={{ fontFamily: DT.decree, fontSize: "26px", color: s.color, letterSpacing: "0.06em" }}
              >
                <AnimatedCounter target={s.value} />
              </motion.span>
              <span style={{ fontFamily: DT.terminal, fontSize: "9px", color: "rgba(210,190,145,1)", letterSpacing: "0.14em", lineHeight: 1.4, textAlign: "center", textShadow: "0 1px 3px rgba(0,0,0,0.95)" }}>{s.label}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── CONTENIDO CENTRAL ── */}
      <div className="flex flex-col gap-3 max-w-4xl relative z-10 px-7 py-5">

        <motion.p
          animate={{ opacity: [0.5, 1, 0.5], letterSpacing: ["0.28em", "0.36em", "0.28em"] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(230,185,60,1)", letterSpacing: "0.28em", textShadow: "0 0 14px rgba(200,160,40,0.95), 0 1px 3px rgba(0,0,0,1)" }}
        >▸ ZONA SEGURA · BÚNKER ACTIVO · ENTRADA LIBRE</motion.p>

        <motion.h3
          animate={{ textShadow: ["0 0 8px rgba(200,120,20,0.4), 0 0 20px rgba(200,120,20,0.15)", "0 0 18px rgba(200,120,20,0.75), 0 0 40px rgba(200,120,20,0.3), 0 0 70px rgba(200,120,20,0.1)", "0 0 8px rgba(200,120,20,0.4), 0 0 20px rgba(200,120,20,0.15)"] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          style={{ fontFamily: DT.notebook, fontSize: "14px", color: "#e8c46a", letterSpacing: "0.22em", textTransform: "uppercase", opacity: 1, textShadow: "0 1px 5px rgba(0,0,0,1), 0 0 22px rgba(210,170,50,0.8)" }}
        >
          Aquí nadien da paso pa' atrás.
        </motion.h3>

        <p style={{ fontFamily: DT.notebook, fontSize: "14.5px", color: "#ffffff", lineHeight: 1.8, letterSpacing: "0.03em", textShadow: "0 1px 4px rgba(0,0,0,0.98), 0 0 10px rgba(0,0,0,0.9)", textTransform: "uppercase" }}>
          Si estás sufriendo acoso o extorsión —{" "}
          <span style={{ color: "rgba(210,70,70,0.95)", fontStyle: "italic" }}>entra al búnker.</span>{" "}
          No importa quién eres ni de dónde vienes: aquí no atendemos solo gente VIP, esto es gratis para todos.{" "}
          Empezamos juntos. Terminamos juntos.{" "}
          <span style={{ color: "#ffffff" }}>Sin miedo. Sin piedad.</span>
        </p>

        {/* Lucifer quote — tarjeta */}
        <motion.div
          className="relative rounded-sm overflow-hidden px-4 py-3 text-left"
          initial={{ opacity: 0, x: -14 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          style={{ background: "rgba(107,0,21,0.1)", border: "1px solid rgba(107,0,21,0.42)" }}
        >
          <motion.div
            style={{ position: "absolute", left: 0, right: 0, height: "100%", background: "linear-gradient(180deg, transparent 0%, rgba(107,0,21,0.06) 50%, transparent 100%)", top: 0 }}
            animate={{ y: ["-100%", "200%"] }} transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 3 }} />
          <div className="flex gap-3 items-start relative z-10">
            <motion.div
              style={{ width: 3, minHeight: 44, background: "linear-gradient(180deg, rgba(107,0,21,0.9), rgba(180,30,50,0.7), rgba(107,0,21,0.9))", flexShrink: 0, borderRadius: 2 }}
              animate={{ boxShadow: ["0 0 4px rgba(107,0,21,0.5)", "0 0 18px rgba(107,0,21,1)", "0 0 4px rgba(107,0,21,0.5)"] }}
              transition={{ duration: 2.2, repeat: Infinity }} />
            <div className="flex flex-col gap-1.5">
              <span style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,1)", letterSpacing: "0.24em", textShadow: "0 0 16px rgba(255,160,40,1), 0 1px 3px rgba(0,0,0,1)" }}>✟ UN DÍA DIJO LUCIFER ✟</span>
              <motion.span
                animate={{ textShadow: ["0 0 6px rgba(255,160,40,0.15)", "0 0 24px rgba(255,160,40,0.95), 0 0 48px rgba(200,120,20,0.45)", "0 0 6px rgba(255,160,40,0.15)"], color: ["rgba(255,160,40,0.9)", "rgba(255,200,70,1)", "rgba(255,160,40,0.9)"] }}
                transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
                style={{ fontFamily: DT.notebook, fontSize: "13.5px", color: "rgba(255,160,40,1)", fontStyle: "italic", lineHeight: 1.55, textTransform: "uppercase" }}
              >
                "De que lloren en tu casa o que lloren en la de él."
              </motion.span>
            </div>
          </div>
        </motion.div>

        {/* Capacidades */}
        <div className="flex flex-wrap gap-1.5 justify-center">
          {[
            { label: "TRIANGULACIÓN Y DECODIFICACIÓN",  icon: Target },
            { label: "INDEXACIÓN ESTRUCTURAL DE DATOS", icon: Search },
            { label: "EXPOSICIÓN DE ACTIVOS OCULTOS",   icon: Eye },
            { label: "HARDWARE DE GRADO MILITAR",       icon: FileText },
          ].map(({ label, icon: Icon }, i) => (
            <motion.div key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm"
              style={{ background: "rgba(107,0,21,0.07)", border: "1px solid rgba(107,0,21,0.28)" }}
              animate={{ borderColor: ["rgba(107,0,21,0.28)", "rgba(107,0,21,0.65)", "rgba(107,0,21,0.28)"], boxShadow: ["0 0 0px transparent", "0 0 12px rgba(107,0,21,0.2)", "0 0 0px transparent"] }}
              transition={{ duration: 2.8 + i * 0.4, repeat: Infinity, delay: i * 0.6 }}
            >
              <Icon size={11} color="rgba(255,160,40,0.8)" />
              <span style={{ fontFamily: DT.terminal, fontSize: "9px", color: "rgba(235,215,175,1)", letterSpacing: "0.14em", textShadow: "0 1px 2px rgba(0,0,0,1)" }}>{label}</span>
            </motion.div>
          ))}
        </div>

        {/* Separador */}
        <div className="flex items-center gap-3">
          <motion.div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.7))" }} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2.8, repeat: Infinity }} />
          <motion.span animate={{ opacity: [0.4, 1, 0.4], rotate: [0, 180, 360] }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }} style={{ color: "rgba(255,160,40,0.8)", fontSize: "10px" }}>✟</motion.span>
          <motion.div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(107,0,21,0.7), transparent)" }} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2.8, repeat: Infinity, delay: 0.6 }} />
        </div>

        {/* Protocolos */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 items-center justify-center">
          {["GRATIS PARA TODOS", "EMPEZAMOS JUNTOS · TERMINAMOS JUNTOS", "SIN MIEDO · SIN PIEDAD"].map((item, i) => (
            <motion.span key={i}
              animate={{ textShadow: ["0 0 8px rgba(235,140,30,0.6)", "0 0 22px rgba(235,140,30,1), 0 0 44px rgba(255,160,40,0.7)", "0 0 8px rgba(235,140,30,0.6)"], opacity: [0.9, 1, 0.9] }}
              transition={{ duration: 2.5 + i * 0.6, repeat: Infinity, delay: i * 0.8 }}
              style={{ fontFamily: DT.terminal, fontSize: "11px", color: "rgba(230,195,80,1)", letterSpacing: "0.12em", textShadow: "0 1px 3px rgba(0,0,0,1)" }}
            >
              {i > 0 && <span style={{ color: "rgba(255,160,40,0.45)", marginRight: 16 }}>·</span>}
              {item}
            </motion.span>
          ))}
        </div>

      </div>

      {/* ── BOTÓN ── */}
      <motion.a
        onClick={() => navigate('/bunker')}
        whileHover={{ scale: 1.04, boxShadow: `0 0 45px rgba(107,0,21,0.9)` }}
        whileTap={{ scale: 0.97 }}
        className="relative z-10 flex items-center gap-2.5 px-10 py-3 rounded-sm mb-6"
        style={{
          background: `linear-gradient(135deg, rgba(107,0,21,0.92), rgba(70,0,10,0.98))`,
          border: `1px solid rgba(107,0,21,0.8)`,
          textDecoration: "none",
          fontFamily: DT.decree,
          fontSize: "15px",
          color: P.parchment,
          letterSpacing: "0.18em",
          boxShadow: "0 0 28px rgba(107,0,21,0.5), 0 0 55px rgba(80,0,10,0.3)",
          textShadow: "0 0 10px rgba(255,255,255,0.45), 0 0 22px rgba(107,0,21,0.35)",
          cursor: "pointer",
        }}
      >
        <motion.div animate={{ filter: ["drop-shadow(0 0 4px rgba(255,255,255,0.4))", "drop-shadow(0 0 10px rgba(255,255,255,0.9))", "drop-shadow(0 0 4px rgba(255,255,255,0.4))"] }} transition={{ duration: 2, repeat: Infinity }}>
          <Shield size={14} />
        </motion.div>
        INGRESAR AL BÚNKER
        <ChevronRight size={12} />
      </motion.a>

      {/* Esquinas decorativas */}
      <div className="absolute top-0 left-0 w-10 h-10" style={{ borderTop: `1px solid rgba(107,0,21,0.8)`, borderLeft: `1px solid rgba(107,0,21,0.8)` }} />
      <div className="absolute top-0 right-0 w-10 h-10" style={{ borderTop: `1px solid rgba(200,120,20,0.55)`, borderRight: `1px solid rgba(200,120,20,0.55)` }} />
      <div className="absolute bottom-0 left-0 w-10 h-10" style={{ borderBottom: `1px solid rgba(200,120,20,0.55)`, borderLeft: `1px solid rgba(200,120,20,0.55)` }} />
      <div className="absolute bottom-0 right-0 w-10 h-10" style={{ borderBottom: `1px solid rgba(107,0,21,0.8)`, borderRight: `1px solid rgba(107,0,21,0.8)` }} />
    </motion.section>
  );
}

// ─── BLOQUE 2 · CARRUSEL DE REFERENCIAS ──────────────────────────────────────
const REFS = [
  { user: "@maria_gm92",   net: "X",         txt: "Llevaba 3 meses con montadeudas. Delta me sacó de eso en 48 horas. Cero drama, todo real.",           stars: 5, color: "#1DA1F2" },
  { user: "@jorge.mx",     net: "Instagram",  txt: "Me hackearon el WhatsApp y les llegaron mensajes a mis clientes. En 6 horas recuperé todo.",            stars: 5, color: "#E1306C" },
  { user: "@lucia_rdz",    net: "Telegram",   txt: "Creía que era imposible ubicar al estafador. Me enviaron el reporte en 24h. Increíble.",               stars: 5, color: "#229ED9" },
  { user: "@emp.acero.mx", net: "Facebook",   txt: "Sufrimos un ataque de ransomware. Delta controló la brecha sin pérdida de datos. 100% profesionales.", stars: 5, color: "#1877F2" },
  { user: "@david_t_mx",   net: "X",         txt: "El Toxic-Check me dio la paz mental que necesitaba. Datos reales, sin drama. Confíen.",                 stars: 5, color: "#1DA1F2" },
  { user: "@carmen.safe",  net: "Instagram",  txt: "Me estaban extorsionando y ni la policía actuó. Delta los rastreó y les puso un alto. GRACIAS.",        stars: 5, color: "#E1306C" },
  { user: "@negocio.vtg",  net: "Facebook",   txt: "Competencia maliciosa nos hackeó la web. Delta identificó el origen y lo neutralizó. Top.",             stars: 5, color: "#1877F2" },
  { user: "@r.pacheco.ok", net: "Telegram",   txt: "Buscaba a un familiar. Protocolo estricto, videollamada previa. Me devolvieron la esperanza.",          stars: 5, color: "#229ED9" },
];

function CarruselReferencias() {
  const trackRef = useRef(null);
  // Duplicamos para el loop infinito
  const items = [...REFS, ...REFS];

  return (
    <div className="w-full overflow-hidden relative" style={{ maskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)" }}>
      <motion.div
        ref={trackRef}
        className="flex gap-4"
        style={{ width: "max-content" }}
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 48, repeat: Infinity, ease: "linear" }}
      >
        {items.map((r, i) => (
          <div
            key={i}
            className="flex-shrink-0 rounded-sm p-3 flex flex-col gap-2"
            style={{
              width: 264,
              background: "rgba(5,3,10,0.95)",
              border: `1px solid rgba(70,50,10,0.35)`,
              backdropFilter: "blur(12px)",
              boxShadow: "0 0 18px rgba(60,40,5,0.15), inset 0 0 10px rgba(0,0,0,0.3)",
            }}
          >
            {/* Header de la tarjeta */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg, rgba(107,0,21,0.6), rgba(6,4,12,0.9))`, border: `1px solid rgba(107,0,21,0.4)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Users size={14} color={`rgba(255,160,40,0.8)`} />
                </div>
                <div>
                  <p style={{ fontFamily: DT.terminal, fontSize: "14px", color: P.parchment, letterSpacing: "0.05em",
                    textShadow: "0 0 10px rgba(255,255,255,0.25)" }}>{r.user}</p>
                  <p style={{ fontFamily: DT.data, fontSize: "14px", color: `rgba(255,255,255,0.82)`, letterSpacing: "0.1em" }}>via {r.net}</p>
                </div>
              </div>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: r.color, boxShadow: `0 0 8px ${r.color}, 0 0 18px ${r.color}66` }} />
            </div>
            {/* Estrellas */}
            <div className="flex gap-1">
              {Array.from({ length: r.stars }).map((_, s) => (
                <Star key={s} size={9} color="rgba(255,160,40,0.9)" fill="rgba(255,160,40,0.9)"
                  style={{ filter: "drop-shadow(0 0 3px rgba(255,160,40,0.7))" }} />
              ))}
            </div>
            {/* Texto */}
            <p style={{ fontFamily: DT.notebook, fontSize: "14px", color: P.parchmentDim, lineHeight: 1.75, letterSpacing: "0.03em" }}>
              "{r.txt}"
            </p>
            {/* Línea de verificación */}
            <div className="flex items-center gap-2 pt-1" style={{ borderTop: `1px solid rgba(60,40,10,0.3)`,
              boxShadow: "0 -1px 8px rgba(60,40,5,0.12)" }}>
              <CheckCircle size={9} color="rgba(155,0,255,0.8)" style={{ filter: "drop-shadow(0 0 4px rgba(155,0,255,0.6))" }} />
              <span style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(155,0,255,0.75)", letterSpacing: "0.1em",
                textShadow: "0 0 8px rgba(155,0,255,0.5)" }}>VERIFICADO</span>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

// ─── BLOQUE 3 · BIBLIOTECA DE MEDIOS ─────────────────────────────────────────
const DOCS = [
  {
    titulo: "CARDING",
    subtitulo: "Operaciones con Tarjetas",
    icono: Zap,
    color: "rgba(220,40,20,0.9)",
    desc: "Guía técnica sobre cómo operan los esquemas de fraude con tarjetas, vectores de ataque y métodos de defensa empresarial.",
    paginas: 47,
    nivel: "AVANZADO",
    id: "DELTA-DOC-001",
  },
  {
    titulo: "TRACKING",
    subtitulo: "Rastreo de Personas",
    icono: Target,
    color: "rgba(255,160,40,0.9)",
    desc: "Metodologías de rastreo digital: metadatos, herramientas de análisis de grado militar, geolocalización pasiva y cómo blindar tu huella digital.",
    paginas: 62,
    nivel: "INTERMEDIO",
    id: "DELTA-DOC-002",
  },
  {
    titulo: "ROBO DE DATOS",
    subtitulo: "Exfiltración & Defensa",
    icono: Lock,
    color: MP.crimsonBright,
    desc: "Anatomía de una fuga de datos: vectores, técnicas de exfiltración, detección temprana y protocolos de contención.",
    paginas: 54,
    nivel: "AVANZADO",
    id: "DELTA-DOC-003",
  },
  {
    titulo: "INTRUSIÓN MÓVIL",
    subtitulo: "Seguridad en Dispositivos",
    icono: Eye,
    color: "rgba(200,120,20,0.9)",
    desc: "Vulnerabilidades en iOS y Android, spyware, keyloggers móviles y guía de hardening para dispositivos personales.",
    paginas: 39,
    nivel: "BÁSICO",
    id: "DELTA-DOC-004",
  },
];

const NIVEL_COLOR = {
  "AVANZADO":   "rgba(220,40,20,0.85)",
  "INTERMEDIO": "rgba(255,160,40,0.85)",
  "BÁSICO":     "rgba(155,0,255,0.8)",
};

function DocCard({ doc, idx }) {
  const [preview, setPreview] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 * idx, duration: 0.5 }}
        className="relative rounded-sm overflow-hidden flex flex-col"
        style={{
          background: "rgba(5,3,10,0.94)",
          border: `1px solid rgba(70,50,10,0.25)`,
        }}
      >
        {/* Miniatura */}
        <div className="relative flex items-center justify-center py-7" style={{
          background: `linear-gradient(135deg, rgba(6,4,12,0.95), rgba(20,8,4,0.9))`,
          borderBottom: `1px solid rgba(60,40,10,0.2)`,
        }}>
          <motion.div
            animate={{ filter: [`drop-shadow(0 0 8px ${doc.color})`, `drop-shadow(0 0 20px ${doc.color})`, `drop-shadow(0 0 8px ${doc.color})`] }}
            transition={{ duration: 3 + idx * 0.4, repeat: Infinity }}
          >
            <doc.icono size={36} color={doc.color} />
          </motion.div>
          {/* Badge nivel */}
          <div className="absolute top-3 right-3 px-2 py-0.5" style={{ background: "rgba(4,2,8,0.9)", border: `1px solid ${NIVEL_COLOR[doc.nivel]}44` }}>
            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: NIVEL_COLOR[doc.nivel], letterSpacing: "0.15em" }}>{doc.nivel}</span>
          </div>
          {/* ID */}
          <div className="absolute top-3 left-3">
            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: `rgba(255,255,255,0.75)`, letterSpacing: "0.1em" }}>{doc.id}</span>
          </div>
        </div>

        {/* Info */}
        <div className="p-4 flex flex-col gap-2 flex-1">
          <div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "14px", color: P.parchment, letterSpacing: "0.08em",
              textShadow: `0 0 14px ${doc.color}, 0 0 30px ${doc.color}55` }}>{doc.titulo}</p>
            <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: `rgba(255,160,40,0.8)`, letterSpacing: "0.12em",
              textShadow: "0 0 10px rgba(255,160,40,0.4)" }}>{doc.subtitulo}</p>
          </div>
          <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "12px", color: P.parchmentDim, lineHeight: 1.6 }}>{doc.desc}</p>
          <div className="flex items-center gap-2">
            <FileText size={9} color={`rgba(255,255,255,0.5)`} />
            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: `rgba(255,255,255,0.5)`, letterSpacing: "0.08em" }}>{doc.paginas} páginas</span>
          </div>
          {/* Botones */}
          <div className="flex gap-2 mt-auto">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setPreview(true)}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-sm"
              style={{ background: "rgba(15,8,4,0.7)", border: `1px solid rgba(200,120,20,0.45)`, cursor: "pointer", fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: `rgba(255,160,40,0.9)`, letterSpacing: "0.1em",
                boxShadow: "0 0 12px rgba(200,120,20,0.2)", textShadow: "0 0 8px rgba(255,160,40,0.45)" }}
            >
              <Play size={9} />
              VISTA PREVIA
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-sm"
              style={{ background: `linear-gradient(135deg, rgba(107,0,21,0.65), rgba(60,0,8,0.85))`, border: `1px solid rgba(107,0,21,0.55)`, cursor: "pointer", fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: P.parchment, letterSpacing: "0.1em",
                boxShadow: "0 0 16px rgba(107,0,21,0.3), 0 0 35px rgba(80,0,10,0.18)", textShadow: "0 0 8px rgba(255,255,255,0.35)" }}
            >
              <Download size={9} />
              DESCARGAR
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Modal de vista previa */}
      <AnimatePresence>
        {preview && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            style={{ background: "rgba(2,1,4,0.93)", backdropFilter: "blur(10px)" }}
            onClick={() => setPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92 }}
              onClick={e => e.stopPropagation()}
              className="relative rounded-sm overflow-hidden"
              style={{ maxWidth: 480, width: "100%", background: "rgba(5,2,8,0.99)", border: `1px solid rgba(107,0,21,0.45)`, boxShadow: `0 0 80px rgba(70,0,10,0.55)` }}
            >
              <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid rgba(107,0,21,0.25)`, background: "rgba(12,3,6,0.9)" }}>
                <div className="flex items-center gap-3">
                  <doc.icono size={14} color={doc.color} />
                  <span style={{ fontFamily: "'Cinzel', serif", fontSize: "14px", color: P.parchment, letterSpacing: "0.1em" }}>{doc.titulo}</span>
                </div>
                <button onClick={() => setPreview(false)} style={{ background: "none", border: "none", cursor: "pointer" }}>
                  <X size={13} color={P.parchmentDim} />
                </button>
              </div>
              <div className="p-6 flex flex-col gap-4">
                <div className="flex items-center gap-3 p-3 rounded-sm" style={{ background: "rgba(10,4,2,0.7)", border: `1px solid rgba(60,40,10,0.2)` }}>
                  <BookOpen size={18} color={doc.color} />
                  <div>
                    <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: P.parchment }}>{doc.id} · {doc.paginas} PÁGINAS</p>
                    <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: `rgba(255,255,255,0.5)`, letterSpacing: "0.1em" }}>NIVEL: {doc.nivel} · CLASIFICADO DELTA</p>
                  </div>
                </div>
                <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: P.parchmentDim, lineHeight: 1.85 }}>{doc.desc}</p>
                <div style={{ height: 1, background: `linear-gradient(90deg, transparent, rgba(107,0,21,0.35), transparent)` }} />
                <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: `rgba(255,255,255,0.68)`, letterSpacing: "0.15em", textAlign: "center" }}>
                  MATERIAL EDUCATIVO · USO ÉTICO · DELTA PROTEC INTEL
                </p>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  className="flex items-center justify-center gap-2 py-3 rounded-sm"
                  style={{ background: `linear-gradient(135deg, rgba(107,0,21,0.75), rgba(60,0,8,0.92))`, border: `1px solid rgba(107,0,21,0.6)`, cursor: "pointer", fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: P.parchment, letterSpacing: "0.12em",
                    boxShadow: "0 0 20px rgba(107,0,21,0.4), 0 0 45px rgba(80,0,10,0.22)",
                    textShadow: "0 0 8px rgba(255,255,255,0.35)" }}
                >
                  <Download size={12} style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.5))" }} />
                  DESCARGAR PDF COMPLETO
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── BLOQUE 4 · PRODUCTOS ─────────────────────────────────────────────────────
const PRODUCTOS = [
  {
    num: "01",
    greek: "ΠΡΩΤΟΚΟΛΛΟ",
    titulo: "ERRATAS · ESTAFADORES · EXTORSIÓN",
    lema: "\"El anonimato es una debilidad del sistema.\"",
    icon: Target,
    color: MP.crimsonBright,
    thumb: errataThumbnail,
    intro: "No existe el crimen perfecto; siempre dejan una firma digital y nosotros tenemos la llave. La Unidad Delta despliega protocolos de interceptación y decodificación de señales para exponer a quienes se esconden en la red.",
    capacidades: [
      { titulo: "TRIANGULACIÓN Y DECODIFICACIÓN", desc: "Interceptamos el punto de origen y los nodos de conexión real detrás de cualquier comunicación." },
      { titulo: "INDEXACIÓN ESTRUCTURAL DE DATOS", desc: "Ejecutamos un escaneo profundo en redes restringidas para extraer expedientes, domicilios y documentación vinculada." },
      { titulo: "EXPOSICIÓN DE ACTIVOS OCULTOS", desc: "Desenterramos el historial completo y los \"trapos sucios\". Los sacamos de sus ratoneras y los dejamos vulnerables ante la verdad." },
    ],
    footer: "Utilizamos sistemas de rastreo táctico y hardware de grado militar que no están disponibles para el sector civil. Si intentaron borrar sus rastros, llegaron tarde: nosotros ya los tenemos indexados.",
  },
  {
    num: "02",
    greek: "ΕΠΙΧΕΙΡΗΣΗ",
    titulo: "CONTRA MONTADEUDAS",
    lema: "\"Sabemos cómo operan, sabemos cómo detenerlos.\"",
    icon: Shield,
    color: "rgba(200,120,20,0.9)",
    thumb: montaThumb,
    intro: "Entendemos la situación. Estás bajo un esquema de extorsión ilegal y tenemos la experiencia necesaria para desmantelar su acoso. Hemos participado en operativos reales contra Call Centers ilegales.",
    capacidades: [
      { titulo: "BAJA DEFINITIVA", desc: "Gestionamos la eliminación de tus datos en sus servidores." },
      { titulo: "PROTECCIÓN DE CONTACTOS", desc: "Blindamos tu agenda para evitar que molesten a tu familia y amigos." },
      { titulo: "CESE DE AMENAZAS", desc: "Detenemos el acoso sin que pagues un solo peso de sus intereses ilegales." },
    ],
    footer: "⚠️ IMPORTANTE: No bloquees a los extorsionadores todavía. Necesitamos las pruebas para rastrear el origen de las amenazas y actuar con precisión.",
  },
  {
    num: "03",
    greek: "ΤΟΞΙΚΟ-ΕΛΕΓΧΟΣ",
    titulo: "TOXIC-CHECK",
    lema: "\"Recupera tu paz mental: la verdad te hará libre.\"",
    icon: Eye,
    color: "rgba(255,160,40,0.85)",
    thumb: toxicThumb,
    intro: "¿Sientes que algo no cuadra? La duda es el peor enemigo de tu tranquilidad. En la Unidad de Inteligencia Delta no nos basamos en suposiciones, nos basamos en evidencia digital forense. Te ayudamos a quitarte la venda con datos reales.",
    capacidades: [
      { titulo: "ANÁLISIS DE HUELLA DIGITAL", desc: "Rastreo de perfiles ocultos en redes sociales y aplicaciones de citas bajo el mismo número o correo." },
      { titulo: "VERIFICACIÓN DE UBICACIÓN", desc: "Validación de historial de geolocalización para confirmar o descartar sospechas de trayectos sospechosos." },
      { titulo: "REPORTE DE PAZ MENTAL", desc: "Informe técnico detallado con capturas, horarios y hechos. Sin dramas, solo la verdad." },
    ],
    footer: "⚠️ AVISO: Operativo estrictamente confidencial. La \"limpieza\" de la investigación garantiza que el objetivo nunca detecte el análisis.",
  },
  {
    num: "04",
    greek: "VIP Πακέτο",
    titulo: "ATENCIÓN DIRECTA CEO",
    lema: "\"Discreción absoluta. Resultados sin compromiso.\"",
    icon: Crown,
    color: "rgba(255,160,40,0.95)",
    thumb: null,
    intro: "Servicio VIP de atención directa del CEO para empresas o personas de élite que requieren investigaciones discretas sobre sus clientes, socios o que están sufriendo ataques masivos.",
    capacidades: [
      { titulo: "ACCESO DIRECTO AL CEO", desc: "Canal cifrado personal con Rood Zapata para casos de máxima confidencialidad." },
      { titulo: "INVESTIGACIONES CORPORATIVAS", desc: "Due diligence digital, análisis de socios, verificación de clientes y blindaje empresarial." },
      { titulo: "RESPUESTA A ATAQUES MASIVOS", desc: "Intervención de emergencia en ataques de gran escala: DDoS, ransomware, espionaje corporativo." },
    ],
    footer: "Este servicio tiene cupo limitado. La comunicación se realiza exclusivamente por canales cifrados tras verificación de identidad 1:1.",
  },
];

function ProductoCard({ prod, idx }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.12 * idx, duration: 0.6 }}
        className="relative rounded-sm overflow-hidden cursor-pointer"
        style={{ background: "rgba(5,3,10,0.94)", border: `1px solid rgba(70,50,10,0.25)` }}
        onClick={() => setOpen(true)}
        whileHover={{ scale: 1.02, y: -3 }}
      >
        {/* Franja de color */}
        <div style={{ height: 2, background: `linear-gradient(90deg, ${prod.color}, transparent)` }} />

        {/* Miniatura del producto */}
        {prod.thumb && (
          <div style={{ position: "relative", width: "100%", height: 140, overflow: "hidden" }}>
            <motion.img
              src={prod.thumb}
              alt={prod.titulo}
              animate={{
                filter: [
                  `brightness(0.78) saturate(0.88) drop-shadow(0 0 0px ${prod.color})`,
                  `brightness(0.92) saturate(1.05) drop-shadow(0 0 8px ${prod.color})`,
                  `brightness(0.78) saturate(0.88) drop-shadow(0 0 0px ${prod.color})`,
                ],
              }}
              transition={{ duration: 4 + idx * 0.5, repeat: Infinity, ease: "easeInOut" }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center top",
                display: "block",
              }}
            />
            {/* Viñeta inferior que funde con el cuerpo */}
            <div style={{
              position: "absolute", inset: 0,
              background: `linear-gradient(180deg,
                rgba(5,3,10,0) 0%,
                rgba(5,3,10,0.08) 50%,
                rgba(5,3,10,0.72) 78%,
                rgba(5,3,10,0.97) 100%)`,
            }} />
            {/* Viñeta lateral izquierda con color del producto */}
            <div style={{
              position: "absolute", inset: 0,
              background: `linear-gradient(90deg, ${prod.color}22 0%, transparent 45%)`,
            }} />
            {/* Número de servicio flotante */}
            <span style={{
              position: "absolute", top: 8, right: 10,
              fontFamily: DT.terminal,
              fontSize: "10px",
              color: prod.color,
              letterSpacing: "0.3em",
              textShadow: `0 0 10px ${prod.color}, 0 0 22px ${prod.color}88`,
              opacity: 0.85,
            }}>
              SERV-{prod.num}
            </span>
          </div>
        )}

        {/* ── Miniatura VIP generada (CSS/Motion, solo cuando thumb es null) ── */}
        {!prod.thumb && (
          <div style={{ position: "relative", width: "100%", height: 140, overflow: "hidden" }}>
            {/* Foto real del CEO como thumbnail VIP */}
            <motion.img
              src={masterclassImg}
              alt="ATENCIÓN DIRECTA CEO"
              animate={{
                filter: [
                  "brightness(0.72) saturate(0.75) sepia(0.22)",
                  "brightness(0.88) saturate(0.95) sepia(0.32)",
                  "brightness(0.72) saturate(0.75) sepia(0.22)",
                ],
              }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 18%", display: "block" }}
            />
            {/* Veladura carmesí lateral izquierda */}
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(107,0,21,0.42) 0%, transparent 55%)" }} />
            {/* Viñeta inferior de fusión */}
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(5,3,10,0) 0%, rgba(5,3,10,0.08) 48%, rgba(5,3,10,0.78) 80%, rgba(5,3,10,0.97) 100%)" }} />
            {/* Línea dorada superior */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent, rgba(255,160,40,0.45), transparent)" }} />
            {/* Badge SERV-04 */}
            <span style={{ position: "absolute", top: 8, right: 10, fontFamily: DT.terminal, fontSize: "10px", color: prod.color, letterSpacing: "0.3em", textShadow: `0 0 10px ${prod.color}, 0 0 22px ${prod.color}88`, opacity: 0.85 }}>
              SERV-{prod.num}
            </span>
            {/* Sello VIP inferior izquierdo */}
            <motion.span
              animate={{ opacity: [0.55, 0.92, 0.55] }}
              transition={{ duration: 3.2, repeat: Infinity }}
              style={{ position: "absolute", bottom: 10, left: 10, fontFamily: DT.decree, fontSize: "11px", color: "rgba(255,160,40,0.9)", letterSpacing: "0.4em", textShadow: "0 0 12px rgba(255,160,40,0.7), 0 0 28px rgba(200,120,20,0.5)" }}
            >
              ✦ VIP ✦
            </motion.span>
          </div>
        )}

        <div className="p-5 flex flex-col gap-3">
          {/* Encabezado: ícono + número */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ filter: [`drop-shadow(0 0 6px ${prod.color})`, `drop-shadow(0 0 16px ${prod.color})`, `drop-shadow(0 0 6px ${prod.color})`] }}
                transition={{ duration: 3 + idx * 0.3, repeat: Infinity }}
              >
                <prod.icon size={24} color={prod.color} />
              </motion.div>
              <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "12px", color: `rgba(255,255,255,0.88)`, letterSpacing: "0.2em" }}>SERV-{prod.num}</span>
            </div>
            <ChevronRight size={14} color={`rgba(255,255,255,0.5)`} />
          </div>

          {/* Nombre griego */}
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: prod.color, letterSpacing: "0.14em",
            textShadow: `0 0 12px ${prod.color}, 0 0 26px ${prod.color}55` }}>{prod.greek}</p>

          {/* Título del servicio */}
          <h4 style={{ fontFamily: "'Cinzel', serif", fontSize: "14px", color: P.parchment, letterSpacing: "0.06em", lineHeight: 1.35,
            textShadow: `0 0 16px ${prod.color}44` }}>{prod.titulo}</h4>

          {/* Lema */}
          <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "12px", color: `rgba(255,160,40,0.88)`, letterSpacing: "0.04em", fontStyle: "italic",
            textShadow: "0 0 10px rgba(255,160,40,0.4)" }}>{prod.lema}</p>

          <div style={{ height: 1, background: `linear-gradient(90deg, ${prod.color}55, transparent)` }} />

          {/* Intro recortado */}
          <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "12px", color: P.parchmentDim, lineHeight: 1.7 }}>
            {prod.intro.slice(0, 120)}…
          </p>

          {/* Capacidades en lista vertical */}
          <div className="flex flex-col gap-2 mt-1">
            {prod.capacidades.map((cap, ci) => (
              <div key={ci} className="flex items-start gap-2">
                <span style={{ color: prod.color, fontSize: "10px", marginTop: 2, flexShrink: 0,
                  textShadow: `0 0 6px ${prod.color}` }}>▸</span>
                <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "12px", color: "rgba(255,255,255,0.85)", lineHeight: 1.5, letterSpacing: "0.02em" }}>
                  <span style={{ color: prod.color, letterSpacing: "0.08em", fontFamily: DT.terminal, fontSize: "11px" }}>{cap.titulo}</span>
                  {" — "}{cap.desc.slice(0, 72)}…
                </p>
              </div>
            ))}
          </div>

          <div style={{ height: 1, background: `linear-gradient(90deg, ${prod.color}33, transparent)`, marginTop: 2 }} />

          <div className="flex items-center gap-2" style={{ color: prod.color, fontFamily: "'Share Tech Mono', monospace", fontSize: "12px", letterSpacing: "0.1em",
            textShadow: `0 0 8px ${prod.color}` }}>
            VER DETALLE COMPLETO <ChevronRight size={10} />
          </div>
        </div>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
            style={{ background: "rgba(2,1,4,0.93)", backdropFilter: "blur(10px)" }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92 }}
              onClick={e => e.stopPropagation()}
              className="relative rounded-sm overflow-hidden overflow-y-auto"
              style={{ maxWidth: 560, width: "100%", maxHeight: "88vh", background: "rgba(5,2,8,0.99)", border: `1px solid rgba(107,0,21,0.45)`, boxShadow: `0 0 80px rgba(70,0,10,0.6)` }}
            >
              <div style={{ height: 3, background: `linear-gradient(90deg, ${prod.color}, rgba(200,120,20,0.6), transparent)` }} />
              <div className="flex items-center justify-between px-5 py-3 sticky top-0 z-10" style={{ borderBottom: `1px solid rgba(107,0,21,0.25)`, background: "rgba(5,2,8,0.98)" }}>
                <div className="flex items-center gap-3">
                  <prod.icon size={16} color={prod.color} />
                  <div>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "14px", color: prod.color, letterSpacing: "0.1em" }}>{prod.greek}</p>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "14px", color: P.parchment, letterSpacing: "0.07em" }}>{prod.titulo}</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={13} color={P.parchmentDim} /></button>
              </div>

              <div className="p-6 flex flex-col gap-5">
                <p style={{ fontFamily: DT.notebook, fontSize: "14px", color: `rgba(255,160,40,0.85)`, letterSpacing: "0.06em", fontStyle: "italic" }}>{prod.lema}</p>
                <p style={{ fontFamily: DT.notebook, fontSize: "14px", color: P.parchmentDim, lineHeight: 1.85 }}>{prod.intro}</p>

                <div style={{ height: 1, background: `linear-gradient(90deg, rgba(107,0,21,0.45), rgba(200,120,20,0.3), transparent)` }} />

                <div className="flex items-center gap-2">
                  <Shield size={11} color={`rgba(255,160,40,0.7)`} />
                  <span style={{ fontFamily: DT.terminal, fontSize: "14px", color: `rgba(255,160,40,0.7)`, letterSpacing: "0.2em" }}>CAPACIDADES DELTA</span>
                </div>

                <div className="flex flex-col gap-3">
                  {prod.capacidades.map((cap, i) => (
                    <div key={i} className="flex gap-3 p-3 rounded-sm" style={{ background: "rgba(10,4,2,0.6)", border: `1px solid rgba(60,40,10,0.2)` }}>
                      <div style={{ width: 4, minWidth: 4, height: 4, borderRadius: "50%", background: prod.color, marginTop: 5 }} />
                      <div>
                        <p style={{ fontFamily: DT.terminal, fontSize: "14px", color: prod.color, letterSpacing: "0.1em", marginBottom: 3,
                          textShadow: `0 0 10px ${prod.color}, 0 0 24px ${prod.color}55` }}>{cap.titulo}</p>
                        <p style={{ fontFamily: DT.notebook, fontSize: "14px", color: P.parchmentDim, lineHeight: 1.7 }}>{cap.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ height: 1, background: `linear-gradient(90deg, transparent, rgba(107,0,21,0.35), transparent)` }} />
                <p style={{ fontFamily: DT.notebook, fontSize: "14px", color: `rgba(255,160,40,0.65)`, lineHeight: 1.8, letterSpacing: "0.04em" }}>{prod.footer}</p>

                <motion.a
                  href="https://t.me/Unit_Intel_Delta_Bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.03 }}
                  className="flex items-center justify-center gap-2 py-3 rounded-sm"
                  style={{ background: `linear-gradient(135deg, rgba(107,0,21,0.8), rgba(60,0,8,0.92))`, border: `1px solid rgba(107,0,21,0.6)`, textDecoration: "none", fontFamily: DT.decree, fontSize: "14px", color: P.parchment, letterSpacing: "0.12em",
                    boxShadow: "0 0 22px rgba(107,0,21,0.45), 0 0 50px rgba(80,0,10,0.25)",
                    textShadow: "0 0 10px rgba(255,255,255,0.4)" }}
                >
                  <Send size={12} style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.5))" }} />
                  SOLICITAR ESTE SERVICIO
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── BLOQUE 5 · FINANZAS ──────────────────────────────────────────────────────
function BloqueFinanzas() {
  const [counter, setCounter] = useState(0);
  const [glitchAmt, setGlitchAmt] = useState(false);
  const [debtPulse, setDebtPulse] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setCounter(c => c + 1), 1000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const fire = () => { setGlitchAmt(true); setTimeout(() => setGlitchAmt(false), 140); };
    const iv = setInterval(fire, 4200);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const iv = setInterval(() => setDebtPulse(p => p + 1), 3000);
    return () => clearInterval(iv);
  }, []);

  const TACTICS = [
    { icon: Shield, label: "Negociación directa con el acreedor", status: "ACTIVO" },
    { icon: Lock,   label: "Blindaje jurídico de tus activos personales", status: "ENCRIPTADO" },
    { icon: Eye,    label: "Análisis forense de contratos abusivos", status: "EN CURSO" },
    { icon: Target, label: "Ruta de salida definitiva trazada en 72h", status: "GARANTIZADO" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="relative rounded-sm overflow-hidden"
      style={{ background: "rgba(4,2,8,0.97)", border: `1px solid rgba(200,120,20,0.55)`, boxShadow: `0 0 70px rgba(90,60,0,0.45), inset 0 0 100px rgba(4,2,0,0.5)` }}
    >
      {/* ── Imagen fondo ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.img
          src={finanzasImg} alt=""
          animate={{ scale: [1, 1.05, 1], filter: ["brightness(0.55) saturate(0.8) sepia(0.18)", "brightness(0.7) saturate(1.0) sepia(0.25)", "brightness(0.55) saturate(0.8) sepia(0.18)"] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "-10%", right: "-5%", width: "65%", height: "120%", objectFit: "cover", objectPosition: "center top" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(4,2,8,1) 0%, rgba(4,2,8,0.97) 32%, rgba(4,2,8,0.82) 55%, rgba(4,2,8,0.35) 78%, rgba(4,2,8,0.15) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(4,2,8,0.85) 0%, transparent 20%, transparent 70%, rgba(4,2,8,0.9) 100%)" }} />
        {/* Halo dorado intenso */}
        <motion.div
          animate={{ opacity: [0.12, 0.35, 0.12], scale: [0.85, 1.12, 0.85] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "50%", right: "22%", transform: "translate(50%,-50%)", width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(ellipse, rgba(170,95,15,0.55) 0%, rgba(107,0,21,0.18) 52%, transparent 72%)", filter: "blur(32px)" }}
        />
        {/* Relámpagos */}
        {[{ right: "28%", delay: 1, dur: 8 }, { right: "42%", delay: 3.5, dur: 11 }, { right: "35%", delay: 6, dur: 14 }].map((r, i) => (
          <motion.div key={i}
            animate={{ opacity: [0, 0.28, 0, 0.12, 0] }}
            transition={{ duration: r.dur, repeat: Infinity, ease: "easeInOut", times: [0, 0.05, 0.1, 0.16, 1], delay: r.delay }}
            style={{ position: "absolute", top: 0, right: r.right, width: i % 2 === 0 ? 1.5 : 1, height: "90%", background: "linear-gradient(180deg, transparent 0%, rgba(200,180,80,0.8) 30%, rgba(140,110,20,0.5) 65%, transparent 100%)", filter: "blur(2px)" }}
          />
        ))}
        {/* Scan line */}
        <motion.div
          className="absolute left-0 right-0 pointer-events-none"
          style={{ height: 1.5, background: "linear-gradient(90deg, transparent, rgba(200,120,20,0.5), rgba(255,160,40,0.3), rgba(200,120,20,0.5), transparent)", zIndex: 5 }}
          animate={{ top: ["-2%", "102%"] }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear", repeatDelay: 4 }}
        />
        {/* Partículas moneda */}
        {[
          { b: "20%", r: "16%", w: 8, h: 8, d: 6, del: 0.5 },
          { b: "38%", r: "30%", w: 5, h: 5, d: 7.5, del: 2 },
          { b: "55%", r: "20%", w: 6, h: 6, d: 9, del: 4 },
          { b: "28%", r: "45%", w: 4, h: 4, d: 5.5, del: 1.5 },
        ].map((p, i) => (
          <motion.div key={i}
            animate={{ y: [0, -20, 0], opacity: [0.1, 0.4, 0.1] }}
            transition={{ duration: p.d, repeat: Infinity, ease: "easeInOut", delay: p.del }}
            style={{ position: "absolute", bottom: p.b, right: p.r, width: p.w, height: p.h, borderRadius: "50%", background: "rgba(255,160,40,0.75)", boxShadow: "0 0 10px rgba(255,160,40,0.9), 0 0 22px rgba(170,95,15,0.6)" }}
          />
        ))}
      </div>

      {/* ── Corner brackets dorados ── */}
      {[{ top: 0, left: 0 }, { top: 0, right: 0 }, { bottom: 0, right: 0 }, { bottom: 0, left: 0 }].map((pos, i) => (
        <motion.div key={i} className="absolute pointer-events-none"
          style={{ ...pos, width: 20, height: 20, zIndex: 8 }}
          initial={{ opacity: 0, scale: 0.3 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d={i === 0 ? "M2 11 L2 2 L11 2" : i === 1 ? "M9 2 L18 2 L18 11" : i === 2 ? "M18 9 L18 18 L9 18" : "M11 18 L2 18 L2 9"}
              stroke="rgba(170,95,15,0.85)" strokeWidth="1.5" strokeLinecap="square" />
          </svg>
        </motion.div>
      ))}

      {/* ── Borde dorado superior ── */}
      <motion.div
        style={{ height: 2, background: "linear-gradient(90deg, rgba(200,120,20,0.9), rgba(255,160,40,0.6), rgba(107,0,21,0.5), transparent)" }}
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 3.5, repeat: Infinity }}
      />

      <div className="relative z-10 p-5 grid grid-cols-1 md:grid-cols-3 gap-5">

        {/* ── Columna izquierda — icono + stats ── */}
        <div className="flex flex-col items-center gap-5">

          {/* Ícono Zap con anillos dorados */}
          <div className="relative flex items-center justify-center" style={{ width: 104, height: 104 }}>
            {[0, 1].map(i => (
              <motion.div key={`${debtPulse}-${i}`}
                className="absolute rounded-full pointer-events-none"
                style={{ border: `1px solid rgba(170,95,15,${0.7 - i * 0.25})`, width: 70 + i * 26, height: 70 + i * 26 }}
                initial={{ opacity: 0.85, scale: 0.6 }}
                animate={{ opacity: 0, scale: 1.5 }}
                transition={{ duration: 2.5, delay: i * 0.5, ease: "easeOut" }}
              />
            ))}
            <motion.div
              animate={{
                filter: ["drop-shadow(0 0 10px rgba(170,95,15,0.7))", "drop-shadow(0 0 30px rgba(255,160,40,0.95))", "drop-shadow(0 0 10px rgba(170,95,15,0.7))"],
                rotate: [0, 6, -6, 0],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <Zap size={48} color="rgba(255,160,40,0.97)" />
            </motion.div>
          </div>

          <div className="text-center flex flex-col gap-1">
            <motion.p
              animate={{ textShadow: ["0 0 10px rgba(170,95,15,0.4)", "0 0 28px rgba(255,160,40,0.9)", "0 0 10px rgba(170,95,15,0.4)"] }}
              transition={{ duration: 3, repeat: Infinity }}
              style={{ fontFamily: DT.decree, fontSize: "17px", color: "rgba(255,160,40,0.97)", letterSpacing: "0.25em" }}
            >FINANZAS</motion.p>
            <motion.p
              animate={{ textShadow: ["0 0 4px rgba(255,255,255,0)", "0 0 14px rgba(255,255,255,0.35)", "0 0 4px rgba(255,255,255,0)"] }}
              transition={{ duration: 4.2, repeat: Infinity, delay: 0.8 }}
              style={{ fontFamily: DT.terminal, fontSize: "11px", color: "rgba(255,255,255,0.5)", letterSpacing: "0.15em" }}
            >UNIDAD ECONÓMICA</motion.p>
          </div>

          <div style={{ width: "100%", height: 1, background: "linear-gradient(90deg, transparent, rgba(200,120,20,0.6), transparent)" }} />

          {/* Métricas financieras en vivo */}
          <div className="flex flex-col gap-2 w-full">
            <div className="flex justify-between items-center">
              <motion.span
                animate={{ textShadow: ["0 0 3px rgba(255,160,40,0)", "0 0 10px rgba(255,160,40,0.6)", "0 0 3px rgba(255,160,40,0)"] }}
                transition={{ duration: 3.5, repeat: Infinity, delay: 0 }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.7)", letterSpacing: "0.1em" }}
              >CASOS ACTIVOS</motion.span>
              <motion.span animate={{ opacity: [1, 0.4, 1], textShadow: ["0 0 4px rgba(80,200,80,0.3)", "0 0 16px rgba(80,200,80,0.9)", "0 0 4px rgba(80,200,80,0.3)"] }} transition={{ duration: 1.3, repeat: Infinity }}
                style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(80,200,80,0.9)" }}>● {47 + (counter % 3)}</motion.span>
            </div>
            <div className="flex justify-between items-center">
              <motion.span
                animate={{ textShadow: ["0 0 3px rgba(255,160,40,0)", "0 0 10px rgba(255,160,40,0.6)", "0 0 3px rgba(255,160,40,0)"] }}
                transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.7)", letterSpacing: "0.1em" }}
              >ÉXITO NEGOCIACIÓN</motion.span>
              <motion.span
                animate={{ textShadow: ["0 0 4px rgba(255,160,40,0.2)", "0 0 18px rgba(255,160,40,0.95)", "0 0 4px rgba(255,160,40,0.2)"] }}
                transition={{ duration: 2.8, repeat: Infinity, delay: 0.3 }}
                style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,160,40,0.95)" }}
              >94.7%</motion.span>
            </div>
            <div className="flex justify-between items-center">
              <motion.span
                animate={{ textShadow: ["0 0 3px rgba(255,160,40,0)", "0 0 10px rgba(255,160,40,0.6)", "0 0 3px rgba(255,160,40,0)"] }}
                transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.7)", letterSpacing: "0.1em" }}
              >TIEMPO MEDIO</motion.span>
              <motion.span
                animate={{ textShadow: ["0 0 4px rgba(255,255,255,0.1)", "0 0 14px rgba(255,255,255,0.6)", "0 0 4px rgba(255,255,255,0.1)"] }}
                transition={{ duration: 3.2, repeat: Infinity, delay: 0.7 }}
                style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,255,255,0.8)" }}
              >72h</motion.span>
            </div>
            <div className="flex justify-between items-center">
              <motion.span
                animate={{ textShadow: ["0 0 3px rgba(255,160,40,0)", "0 0 10px rgba(255,160,40,0.6)", "0 0 3px rgba(255,160,40,0)"] }}
                transition={{ duration: 3.5, repeat: Infinity, delay: 1.5 }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.7)", letterSpacing: "0.1em" }}
              >DEUDA NEUTRALIZADA</motion.span>
              <motion.span
                animate={glitchAmt
                  ? { x: [-2, 2, 0], skewX: [-4, 4, 0] } : undefined}
                transition={glitchAmt ? {} : undefined}
                style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(180,20,40,0.9)" }}
              >{glitchAmt ? "$█.██M" : "$3.2M"}</motion.span>
            </div>
          </div>

          {/* Barra de presión activa */}
          <div className="w-full flex flex-col gap-1">
            <div className="flex justify-between">
              <motion.span
                animate={{ textShadow: ["0 0 4px rgba(107,0,21,0.2)", "0 0 14px rgba(107,0,21,0.85)", "0 0 4px rgba(107,0,21,0.2)"] }}
                transition={{ duration: 2.4, repeat: Infinity }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: MP.crimsonBright, letterSpacing: "0.1em" }}
              >PRESIÓN ACTIVA</motion.span>
              <motion.span
                animate={{ textShadow: ["0 0 3px rgba(255,160,40,0)", "0 0 12px rgba(255,160,40,0.75)", "0 0 3px rgba(255,160,40,0)"] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1.2 }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.7)" }}
              >BLOQUEADA</motion.span>
            </div>
            <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden" }}>
              <motion.div
                style={{ height: "100%", background: "linear-gradient(90deg, rgba(107,0,21,0.9), rgba(180,30,50,0.7), rgba(255,160,40,0.5))", borderRadius: 2 }}
                animate={{ width: ["60%", "15%", "40%", "8%", "55%"] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </div>
        </div>

        {/* ── Columna central+derecha — texto + tácticas ── */}
        <div className="md:col-span-2 flex flex-col gap-5">

          {/* Header */}
          <div className="flex flex-col gap-1">
            <motion.p
              animate={{ textShadow: ["0 0 3px rgba(255,160,40,0)", "0 0 12px rgba(255,160,40,0.65)", "0 0 3px rgba(255,160,40,0)"] }}
              transition={{ duration: 3.8, repeat: Infinity, delay: 0.4 }}
              style={{ fontFamily: DT.terminal, fontSize: "11px", color: "rgba(255,160,40,0.7)", letterSpacing: "0.25em" }}
            >
              BLOQUE V · ASESORÍA FINANCIERA TÁCTICA
            </motion.p>
            <motion.h4
              animate={{ textShadow: ["0 0 8px rgba(200,120,20,0.3)", "0 0 24px rgba(255,160,40,0.75), 0 0 50px rgba(100,70,0,0.3)", "0 0 8px rgba(200,120,20,0.3)"] }}
              transition={{ duration: 4, repeat: Infinity }}
              style={{ fontFamily: DT.decree, fontSize: "16px", color: "rgba(255,255,255,0.97)", letterSpacing: "0.12em" }}
            >
              ESTRATEGIA DE SALIDA · MONTADEUDAS
            </motion.h4>
          </div>

          <motion.div
            style={{ height: 1, background: "linear-gradient(90deg, rgba(200,120,20,0.8), rgba(255,160,40,0.4), transparent)" }}
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 3.5, repeat: Infinity }}
          />

          <p style={{ fontFamily: DT.terminal, fontSize: "13px", color: "rgba(255,255,255,0.88)", lineHeight: 1.7, letterSpacing: "0.04em" }}>
            El sistema está diseñado para que te asfixies. Las apps de montadeudas no buscan tu pago, buscan tu
            <motion.span style={{ color: "rgba(180,20,40,0.95)", fontWeight: "bold" }}
              animate={{ textShadow: ["0 0 4px rgba(107,0,21,0.3)", "0 0 14px rgba(107,0,21,0.8)", "0 0 4px rgba(107,0,21,0.3)"] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            > control</motion.span>.
            No caigas en su juego. Nuestro equipo no solo negocia —{" "}
            <motion.span
              animate={{ textShadow: ["0 0 6px rgba(255,160,40,0.2)", "0 0 18px rgba(255,160,40,0.8)", "0 0 6px rgba(255,160,40,0.2)"] }}
              transition={{ duration: 3, repeat: Infinity }}
              style={{ color: "rgba(255,160,40,0.97)" }}
            >desarticula la presión y traza la ruta de salida definitiva</motion.span>.
            Si ellos ya tienen tus datos, tú necesitas nuestra estrategia antes de que el ruido se vuelva insoportable.
          </p>

          {/* Tácticas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TACTICS.map((tac, i) => (
              <motion.div
                key={i}
                className="flex items-start gap-3 p-3 rounded-sm"
                style={{ background: "rgba(8,5,2,0.8)", border: "1px solid rgba(200,120,20,0.25)", borderLeft: "2px solid rgba(200,120,20,0.6)", borderColor: "rgba(200,120,20,0.25)" }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ x: 3, borderColor: "rgba(255,160,40,0.5)" }}
                animate={{ boxShadow: ["inset 0 0 0px rgba(200,120,20,0)", "inset 0 0 14px rgba(200,120,20,0.12)", "inset 0 0 0px rgba(200,120,20,0)"] }}
              >
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.25, 1] }}
                  transition={{ duration: 2.5 + i * 0.3, repeat: Infinity, delay: i * 0.4 }}
                >
                  <tac.icon size={12} color="rgba(170,95,15,0.9)" />
                </motion.div>
                <div className="flex flex-col gap-0.5 flex-1">
                  <motion.span
                    animate={{ textShadow: ["0 0 3px rgba(255,255,255,0)", "0 0 12px rgba(255,255,255,0.5)", "0 0 3px rgba(255,255,255,0)"] }}
                    transition={{ duration: 3 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}
                    style={{ fontFamily: DT.terminal, fontSize: "12px", color: "rgba(255,255,255,0.88)", letterSpacing: "0.04em", lineHeight: 1.4 }}
                  >{tac.label}</motion.span>
                  <motion.span
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.5 }}
                    style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.7)", letterSpacing: "0.12em" }}
                  >▸ {tac.status}</motion.span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* ADVERTENCIA tipo REGLA DE ORO */}
          <motion.div
            className="rounded-sm p-4"
            style={{ background: "rgba(12,6,0,0.85)", border: "1px solid rgba(200,120,20,0.35)", borderLeft: "3px solid rgba(170,95,15,0.85)" }}
            animate={{ boxShadow: ["inset 0 0 0px rgba(200,120,20,0)", "inset 0 0 24px rgba(200,120,20,0.2)", "inset 0 0 0px rgba(200,120,20,0)"] }}
            transition={{ duration: 4.5, repeat: Infinity }}
          >
            <motion.p
              animate={{ textShadow: ["0 0 4px rgba(255,160,40,0.2)", "0 0 16px rgba(255,160,40,0.85)", "0 0 4px rgba(255,160,40,0.2)"] }}
              transition={{ duration: 2.8, repeat: Infinity }}
              style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,160,40,0.97)", letterSpacing: "0.14em", marginBottom: 6 }}
            >
              ⚠ PROTOCOLO DE CONSULTA
            </motion.p>
            <p style={{ fontFamily: DT.terminal, fontSize: "13px", color: "rgba(255,255,255,0.85)", lineHeight: 1.85, letterSpacing: "0.04em" }}>
              La primera consulta es <motion.strong
                animate={{ textShadow: ["0 0 4px rgba(255,160,40,0.2)", "0 0 16px rgba(255,160,40,0.9)", "0 0 4px rgba(255,160,40,0.2)"] }}
                transition={{ duration: 2.6, repeat: Infinity }}
                style={{ color: "rgba(255,160,40,0.97)" }}
              >completamente gratuita y confidencial</motion.strong>. Evaluamos tu caso, identificamos las vulnerabilidades del acreedor y diseñamos la estrategia de neutralización en 72 horas.
            </p>
          </motion.div>

          {/* Botón con shimmer dorado */}
          <div className="flex items-center gap-4">
            <motion.a
              href="https://t.me/Unit_Intel_Delta_Bot"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, boxShadow: "0 0 50px rgba(170,95,15,0.85)" }}
              whileTap={{ scale: 0.97 }}
              className="self-start flex items-center gap-3 px-7 py-3 rounded-sm"
              style={{ background: "linear-gradient(135deg, rgba(100,70,5,0.85), rgba(50,35,0,0.98))", border: "1px solid rgba(180,130,20,0.5)", textDecoration: "none", fontFamily: DT.decree, fontSize: "14px", color: "rgba(255,255,255,0.97)", letterSpacing: "0.15em", position: "relative", overflow: "hidden" }}
            >
              <motion.div
                style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent, rgba(255,160,40,0.12), transparent)", pointerEvents: "none" }}
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 2.5 }}
              />
              <motion.div animate={{ scale: [1, 1.3, 1], filter: ["drop-shadow(0 0 3px rgba(255,160,40,0.5))", "drop-shadow(0 0 10px rgba(255,160,40,0.95))", "drop-shadow(0 0 3px rgba(255,160,40,0.5))"] }}
                transition={{ duration: 2, repeat: Infinity }}>
                <Send size={12} />
              </motion.div>
              <motion.span
                animate={{ textShadow: ["0 0 4px rgba(255,255,255,0.1)", "0 0 18px rgba(255,255,255,0.7)", "0 0 4px rgba(255,255,255,0.1)"] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >CONSULTA SIN COSTO</motion.span>
            </motion.a>
            <div className="flex flex-col gap-0.5">
              <motion.span
                animate={{ textShadow: ["0 0 3px rgba(255,160,40,0)", "0 0 10px rgba(255,160,40,0.65)", "0 0 3px rgba(255,160,40,0)"] }}
                transition={{ duration: 3.4, repeat: Infinity }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.6)", letterSpacing: "0.1em" }}
              >RESPUESTA</motion.span>
              <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.6, repeat: Infinity }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.9)" }}>
                &lt; 2h ●
              </motion.span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── BLOQUE 6 · MASTERCLASS ───────────────────────────────────────────────────
const WEEKS = [
  { n: "01", t: "DIAGNÓSTICO",       d: "Evaluación de tu nivel actual y activación de la herramienta Omniscient Observer desde la primera instancia." },
  { n: "02", t: "VECTORES DE ATAQUE",d: "Comprensión profunda de cómo piensa y opera el atacante. Reconocimiento de patrones y señales tempranas." },
  { n: "03", t: "DEFENSA ACTIVA",    d: "Implementación de protocolos personalizados. Blindaje de identidad digital, dispositivos y comunicaciones." },
  { n: "04", t: "INTELIGENCIA TÁCTICA & RASTREO",   d: "Técnicas de interceptación y análisis con herramientas de grado militar para detectar y neutralizar amenazas antes de que escalen." },
  { n: "05", t: "MAESTRÍA TOTAL",    d: "Certificación interna. El usuario sale siendo experto en la materia y listo para operar de forma autónoma." },
];

function BloqueMasterclass() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", email: "", motivo: "" });

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="relative rounded-sm overflow-hidden flex flex-col"
        style={{ background: "rgba(4,2,8,0.98)", border: "1px solid rgba(107,0,21,0.4)", boxShadow: "0 0 80px rgba(50,0,8,0.5)" }}
      >
        {/* ══ MINIATURA CINEMATOGRÁFICA ══════════════════════════════════════ */}
        <div className="relative overflow-hidden" style={{ minHeight: "clamp(200px, 28vw, 360px)" }}>

          {/* Imagen de fondo — Rood Zapata */}
          <img
            src={masterclassImg}
            alt="Rood Zapata · Masterclass"
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "cover", objectPosition: "center top", opacity: 0.93 }}
          />

          {/* Gradiente de profundidad — efecto cine */}
          <div className="absolute inset-0" style={{
            background: `
              linear-gradient(to bottom,
                rgba(4,2,8,0.5)  0%,
                rgba(4,2,8,0.05) 30%,
                rgba(4,2,8,0.08) 50%,
                rgba(4,2,8,0.6)  75%,
                rgba(4,2,8,0.98) 100%
              ),
              linear-gradient(to right,
                rgba(4,2,8,0.55) 0%,
                transparent 18%,
                transparent 82%,
                rgba(4,2,8,0.55) 100%
              )
            `,
          }} />

          {/* Scanlines sutiles */}
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.04) 3px, rgba(0,0,0,0.04) 4px)",
            zIndex: 2,
          }} />

          {/* Overlay pulsante carmesí */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{ opacity: [0.1, 0.32, 0.1] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            style={{ background: "radial-gradient(ellipse 55% 45% at 50% 65%, rgba(107,0,21,0.3), transparent 70%)", zIndex: 2 }}
          />

          {/* ── BADGE SUPERIOR ─────────────────────────────────────────────── */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 pt-5 z-10">
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ opacity: [1, 0.25, 1] }}
                transition={{ duration: 1.6, repeat: Infinity }}
                style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(155,0,255,0.9)", boxShadow: "0 0 10px rgba(155,0,255,0.8)" }}
              />
              <span style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(155,0,255,0.85)", letterSpacing: "0.22em" }}>
                INSCRIPCIONES ABIERTAS
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 rounded-sm" style={{ background: "rgba(4,2,8,0.82)", border: "1px solid rgba(255,160,40,0.35)" }}>
              <Crown size={10} color="rgba(255,160,40,0.88)" />
              <span style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,160,40,0.88)", letterSpacing: "0.15em" }}>MASTERCLASS VIP</span>
            </div>
          </div>

          {/* ── NOMBRE DEL CEO sobre la imagen ──────────────────────────────── */}
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-6 z-10 flex flex-col gap-2">
            <motion.div
              initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }}
              viewport={{ once: true }} transition={{ duration: 1.2 }}
              style={{ height: 1, background: "linear-gradient(90deg, rgba(200,120,20,0.85), rgba(255,160,40,0.55), transparent)", marginBottom: 10 }}
            />

            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div className="flex flex-col gap-1">
                <motion.h3
                  animate={{ textShadow: ["0 0 30px rgba(107,0,21,0.5)", "0 0 90px rgba(107,0,21,0.9), 0 0 150px rgba(80,0,12,0.3)", "0 0 30px rgba(107,0,21,0.5)"] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  style={{ fontFamily: DT.greek, fontSize: "18px", color: MP.parchment, letterSpacing: "0.12em", lineHeight: 1 }}
                >
                  ROOD ZAPATA
                </motion.h3>
                <motion.p
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 3.5, repeat: Infinity }}
                  style={{ fontFamily: DT.greek, fontSize: "14px", color: "rgba(255,160,40,0.88)", letterSpacing: "0.06em" }}
                >
                  Υηρρεσιά
                </motion.p>
                <p style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(255,255,255,0.42)", letterSpacing: "0.22em", marginTop: 4 }}>
                  ACOMPAÑAMIENTO INDIVIDUAL · 5 SEMANAS · CEO PERSONAL
                </p>
              </div>

              {/* Mini-stats */}
              <div className="flex flex-col gap-1.5 items-end">
                {[
                  { label: "DURACIÓN",  val: "5 SEMANAS"   },
                  { label: "FORMATO",   val: "1 A 1"        },
                  { label: "GUÍA",      val: "CEO DIRECTO"  },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2 px-3 py-1 rounded-sm" style={{ background: "rgba(4,2,8,0.88)", border: "1px solid rgba(107,0,21,0.28)" }}>
                    <span style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(255,255,255,0.82)", letterSpacing: "0.16em" }}>{item.label}:</span>
                    <span style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,160,40,0.82)", letterSpacing: "0.12em" }}>{item.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Corners sobre la imagen */}
          <div className="absolute top-0 left-0 w-10 h-10 z-10" style={{ borderTop: "1px solid rgba(255,160,40,0.5)", borderLeft: "1px solid rgba(107,0,21,0.7)" }} />
          <div className="absolute top-0 right-0 w-10 h-10 z-10" style={{ borderTop: "1px solid rgba(255,160,40,0.5)", borderRight: "1px solid rgba(107,0,21,0.7)" }} />
        </div>

        {/* ══ PANEL DE CONTENIDO ══════════════════════════════════════════════ */}
        <div className="p-5 flex flex-col gap-4">
          <p style={{ fontFamily: DT.notebook, fontSize: "13.5px", color: P.parchmentDim, lineHeight: 1.7, letterSpacing: "0.04em" }}>
            Formación intensiva en seguridad digital, caminando al lado del CEO. Activación de la herramienta{" "}
            <em style={{ color: P.gold }}>Omniscient Observer</em> desde la primera instancia. Sesiones individuales — nunca grupales. El usuario sale siendo experto en la materia.
          </p>

          {/* Timeline semanal */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {WEEKS.map((w, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
                whileHover={{ scale: 1.03, y: -2 }}
                className="flex flex-col gap-1.5 p-2.5 rounded-sm"
                style={{ background: "rgba(10,4,2,0.65)", border: "1px solid rgba(60,40,10,0.28)",
                  boxShadow: "0 0 14px rgba(50,30,0,0.12), inset 0 0 8px rgba(0,0,0,0.3)" }}
              >
                <div className="flex items-center gap-2">
                  <motion.span
                    animate={{ textShadow: ["0 0 8px rgba(107,0,21,0.4)", "0 0 22px rgba(107,0,21,0.8)", "0 0 8px rgba(107,0,21,0.4)"] }}
                    transition={{ duration: 3 + i * 0.3, repeat: Infinity }}
                    style={{ fontFamily: DT.greek, fontSize: "14px", color: "rgba(255,160,40,0.8)", lineHeight: 1 }}
                  >{w.n}</motion.span>
                  <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(107,0,21,0.4), transparent)" }} />
                </div>
                <p style={{ fontFamily: DT.decree, fontSize: "14px", color: "rgba(255,160,40,0.85)", letterSpacing: "0.1em",
                  textShadow: "0 0 12px rgba(255,160,40,0.45), 0 0 28px rgba(200,120,20,0.25)" }}>{w.t}</p>
                <p style={{ fontFamily: DT.data, fontSize: "12px", color: P.parchmentDim, lineHeight: 1.6 }}>{w.d}</p>
              </motion.div>
            ))}
          </div>

          <div style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.45), rgba(200,120,20,0.3), transparent)" }} />

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p style={{ fontFamily: DT.notebook, fontSize: "13px", color: "rgba(255,255,255,0.48)", letterSpacing: "0.05em", fontStyle: "italic", maxWidth: 380 }}>
              "Caminar al lado del CEO es acceso directo al conocimiento que construyó este sistema desde adentro."
            </p>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 45px rgba(107,0,21,0.75), 0 0 90px rgba(80,0,10,0.3)" }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setOpen(true)}
              className="flex items-center gap-3 px-9 py-3.5 rounded-sm flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, rgba(107,0,21,0.9), rgba(50,0,5,0.99))",
                border: "1px solid rgba(107,0,21,0.7)",
                cursor: "pointer",
                fontFamily: DT.decree,
                fontSize: "14px",
                color: P.parchment,
                letterSpacing: "0.18em",
                boxShadow: "0 0 22px rgba(107,0,21,0.45), 0 0 50px rgba(60,0,8,0.28)",
                textShadow: "0 0 10px rgba(255,255,255,0.4)",
              }}
            >
              <Star size={13} style={{ filter: "drop-shadow(0 0 5px rgba(255,255,255,0.6))" }} />
              POSTULAR A LA MASTERCLASS
            </motion.button>
          </div>
        </div>

        <div style={{ height: 2, background: "linear-gradient(90deg, rgba(107,0,21,0.7), rgba(200,120,20,0.5), rgba(107,0,21,0.7))" }} />
      </motion.div>

      {/* ══ MODAL DE POSTULACIÓN ════════════════════════════════════════════════ */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            style={{ background: "rgba(2,1,4,0.95)", backdropFilter: "blur(12px)" }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92 }}
              onClick={e => e.stopPropagation()}
              className="relative rounded-sm overflow-hidden"
              style={{ maxWidth: 480, width: "100%", background: "rgba(5,2,8,0.99)", border: "1px solid rgba(107,0,21,0.5)", boxShadow: "0 0 100px rgba(70,0,10,0.7)" }}
            >
              {/* Header del modal con miniatura */}
              <div className="relative overflow-hidden" style={{ height: 120 }}>
                <img src={masterclassImg} alt="" className="absolute inset-0 w-full h-full" style={{ objectFit: "cover", objectPosition: "center 20%", opacity: 0.62 }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(4,2,8,0.35), rgba(4,2,8,0.96))" }} />
                <div className="absolute inset-0 flex items-end justify-between px-5 pb-3 z-10">
                  <div>
                    <p style={{ fontFamily: DT.greek, fontSize: "14px", color: MP.parchment, letterSpacing: "0.1em" }}>ROOD ZAPATA</p>
                    <p style={{ fontFamily: DT.decree, fontSize: "14px", color: "rgba(255,160,40,0.8)", letterSpacing: "0.18em" }}>FORMULARIO DE POSTULACIÓN</p>
                  </div>
                  <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}>
                    <X size={14} color={P.parchmentDim} />
                  </button>
                </div>
              </div>

              <div className="p-6 flex flex-col gap-4">
                {[
                  { k: "nombre", label: "NOMBRE COMPLETO",   type: "text", ph: "Tu nombre real..." },
                  { k: "email",  label: "CORREO / TELEGRAM", type: "text", ph: "@usuario o correo..." },
                ].map(f => (
                  <div key={f.k} className="flex flex-col gap-1.5">
                    <label style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,160,40,0.75)", letterSpacing: "0.15em" }}>{f.label}</label>
                    <input
                      type={f.type}
                      placeholder={f.ph}
                      value={form[f.k]}
                      onChange={e => setForm(prev => ({ ...prev, [f.k]: e.target.value }))}
                      style={{ background: "rgba(10,4,2,0.8)", border: "1px solid rgba(107,0,21,0.3)", borderRadius: 2, padding: "8px 12px", fontFamily: DT.data, fontSize: "14px", color: P.parchment, outline: "none", letterSpacing: "0.04em" }}
                    />
                  </div>
                ))}
                <div className="flex flex-col gap-1.5">
                  <label style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,160,40,0.75)", letterSpacing: "0.15em" }}>¿POR QUÉ QUIERES ENTRAR?</label>
                  <textarea
                    placeholder="Cuéntame tu situación..."
                    value={form.motivo}
                    onChange={e => setForm(prev => ({ ...prev, motivo: e.target.value }))}
                    rows={3}
                    style={{ background: "rgba(10,4,2,0.8)", border: "1px solid rgba(107,0,21,0.3)", borderRadius: 2, padding: "8px 12px", fontFamily: DT.data, fontSize: "14px", color: P.parchment, outline: "none", resize: "none", letterSpacing: "0.04em", lineHeight: 1.7 }}
                  />
                </div>
                <div style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.35), transparent)" }} />
                <p style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(255,255,255,0.72)", letterSpacing: "0.14em", textAlign: "center" }}>
                  CUPO LIMITADO · SELECCIÓN DIRECTA DEL CEO · VERIFICACIÓN 1:1
                </p>
                <motion.a
                  href="https://wa.me/message/XXXXXXXXXX"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.03, boxShadow: "0 0 30px rgba(107,0,21,0.6)" }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center justify-center gap-2 py-3 rounded-sm"
                  style={{ background: "linear-gradient(135deg, rgba(107,0,21,0.8), rgba(60,0,8,0.95))", border: "1px solid rgba(107,0,21,0.5)", cursor: "pointer", fontFamily: DT.decree, fontSize: "14px", color: P.parchment, letterSpacing: "0.15em", textDecoration: "none" }}
                >
                  <Send size={12} />
                  ENVIAR POSTULACIÓN
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── BLOQUE 7 · LEY DE HIERRO ─────────────────────────────────────────────────
const LEYES = [
  { num: "I",    titulo: "Reclutamiento por Selección Directa",    txt: "El equipo de Delta Protec no se forma por medios convencionales. Cada operativo fue identificado y rescatado de situaciones críticas por la dirección. Son profesionales unidos por un propósito de transformación y una lealtad absoluta." },
  { num: "II",   titulo: "Aislamiento Total · Células Fantasma",    txt: "Nuestra estructura es una red de puntos ciegos. Ningún operativo conoce la identidad, ubicación o rostro de sus compañeros. No existe comunicación horizontal; si un eslabón fuera comprometido, no tiene nada que entregar." },
  { num: "III",  titulo: "Zero Indexing · Sin Rastro Digital",      txt: "No utilizamos plataformas ni servicios de nube que puedan ser indexados. Nuestra infraestructura no es investigable porque no reside en espacios públicos ni comerciales. Lo que no existe, no puede ser vulnerado." },
  { num: "IV",   titulo: "Cifrado Extremo y Flujo Volátil",         txt: "Toda la comunicación se maneja mediante protocolos de encriptación de grado militar. La información fluye en tiempo real y se destruye inmediatamente. No hay servidores con bases de datos; lo que no se almacena, no puede ser hackeado." },
  { num: "V",    titulo: "Monitoreo Táctico 24/7",                  txt: "La vigilancia interna es total. Cada dispositivo y cada operativo están monitoreados las 24 horas, los 7 días de la semana, sin excepción. Aquí no hay margen de error." },
  { num: "VI",   titulo: "Confidencialidad y Consecuencias Penales", txt: "Por contrato, el personal tiene prohibido revelar funciones o métodos operativos. Quien rompa esta confianza traiciona al proyecto, al equipo y a su familia, y enfrentará de inmediato el peso de la ley." },
  { num: "VII",  titulo: "Validación Final del CEO",                 txt: "Ninguna solución o reporte es liberado sin pasar por mi escritorio. Superviso cada detalle técnico y operativo personalmente. Yo soy el filtro final que garantiza la perfección y la discreción total." },
];

const MISION_VISION = [
  {
    num: "01", titulo: "MI MISIÓN · EL PARO",
    txt: "Aquí no andamos con cuentos ni palabras bonitas: mi misión en Delta Protec es sacarte del hoyo. Si te están extorsionando, acosando o te tienen contra la pared, yo entro al quite para que recuperes el control. La ayuda en la Zona Segura es totalmente gratis porque sé lo que es sentirse vulnerable y no voy a lucrar con tu miedo.",
  },
  {
    num: "02", titulo: "MI VISIÓN · EL MURO",
    txt: "Quiero que Delta Protec sea el lugar donde los cobardes que se esconden tras una pantalla se topen con pared. Mi visión es que cualquier persona tenga un escudo real y acceso a defensa de alto nivel. El rescate de emergencia es de mi cuenta, pero si lo que buscas es blindar tu vida digital para ser inalcanzable, para eso he diseñado mis herramientas.",
  },
  {
    num: "03", titulo: "MI HISTORIA · LA REDENCIÓN",
    txt: "Yo estuve del otro lado. Conozco las entrañas del carding, el phishing y el rastreo porque yo fui parte de ese mundo. Pero cuando nacieron mis hijos, mi vida dio un giro de 360 grados. Me hice responsable de mis actos por voluntad propia: yo solito me entregué y rendí cuentas. Hoy, con la frente en alto y la cuenta saldada, uso toda esa experiencia para protegerte.",
  },
];

const VALORES = [
  { titulo: "Altruismo en Crisis",      desc: "Si estás bajo ataque, mi apoyo es gratuito. Punto." },
  { titulo: "Honestidad Brutal",         desc: "No te vendo humo. Te digo las cosas como son y te doy soluciones que funcionan." },
  { titulo: "Confidencialidad de Búnker",desc: "Tu historia y tus datos se mueren conmigo. Tu privacidad es sagrada." },
  { titulo: "Lealtad al Usuario",        desc: "Aquí no eres un cliente más; eres alguien que confió en mí, y yo no dejo a nadie atrás." },
];

function BloqueLeyDeHierro() {
  const [tabCeo, setTabCeo] = useState("historia");

  return (
    <div className="flex flex-col gap-8">
      {/* Historia del CEO */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative rounded-sm overflow-hidden"
        style={{ background: "rgba(5,3,10,0.95)", border: `1px solid rgba(200,120,20,0.3)` }}
      >
        <div style={{ height: 2, background: `linear-gradient(90deg, rgba(200,120,20,0.7), rgba(107,0,21,0.5), transparent)` }} />

        {/* Tabs */}
        <div className="flex" style={{ borderBottom: `1px solid rgba(60,40,10,0.25)` }}>
          {(([["historia", "HISTORIA DEL CEO"], ["leyes", "LEY DE HIERRO"]] )).map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTabCeo(k)}
              style={{
                flex: 1,
                padding: "12px 16px",
                background: tabCeo === k ? "rgba(15,8,4,0.8)" : "transparent",
                border: "none",
                borderBottom: tabCeo === k ? `2px solid rgba(107,0,21,0.7)` : "2px solid transparent",
                cursor: "pointer",
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: "14px",
                color: tabCeo === k ? `rgba(255,160,40,0.9)` : `rgba(255,255,255,0.4)`,
                letterSpacing: "0.2em",
                transition: "all 0.3s",
              }}
            >{l}</button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {tabCeo === "historia" ? (
            <motion.div
              key="historia"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="p-5 flex flex-col gap-4"
            >
              {/* Narrativa */}
              <div className="flex flex-col gap-4">
                {[
                  "El origen de este sistema no nace de la teoría, sino del asfalto digital más profundo. Durante años, Rood Zapata operó en las sombras de los grupos de Telegram, donde no solo dominó las artes del carding, el phishing y el rastreo de información, sino que fue el creador original de muchos de los métodos que hoy son tendencia en la red. Fue el arquitecto de tácticas que se convirtieron en el estándar del submundo digital.",
                  "Sin embargo, el descenso personal tuvo un costo. Llegó el punto crítico donde todo se fue al carajo y el vacío fue total; el peso de ese mundo terminó por pasar factura. En ese muro, el único ancla real fueron sus hijos. Ese pensamiento provocó un giro de 360 grados. Entendió que ser padre y ser madre es ser el escudo de los suyos.",
                  "Fue entonces cuando esa misma genialidad técnica y la fama de sus creaciones orilló a una empresa de ciberseguridad a reclutarlo. En un acto de congruencia con su transformación, Rood se encargó personalmente de ayudar a las empresas a bloquear esos mismos métodos y a parchar las vulnerabilidades que él mismo había diseñado, cerrando para siempre las puertas que antes ayudó a abrir.",
                  "Hoy, tras años de trayectoria en la industria legal, utiliza ese ingenio para abrirle los ojos a una sociedad dormida y proteger a los niños de los depredadores que acechan. Porque tras cruzar el infierno, su código es inquebrantable.",
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "13px", color: P.parchmentDim, lineHeight: 1.7, letterSpacing: "0.03em" }}>{p}</p>
                ))}
              </div>

              {/* Cita */}
              <blockquote className="relative p-5 rounded-sm border border-solid border-l-[3px]" style={{ background: "rgba(10,4,2,0.7)", borderTopColor: "#6b00154d", borderRightColor: "#6b00154d", borderBottomColor: "#6b00154d", borderLeftColor: "#6b0015b3" }}>
                <p style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: `rgba(255,160,40,0.8)`, lineHeight: 1.7, letterSpacing: "0.04em", fontStyle: "italic" }}>
                  "Del fuego vengo y al fuego voy. Perdí el miedo, perdí los clasicismos, perdí las malas prácticas del poder, perdí amigos, disque familia... y los que me querían ver morir, murieron antes que yo, pero por sí mismos. Al final entendí que el mentir nunca te va a dejar nada bueno, que la mitomanía es lo peor que puede existir y que la paz mental es primero."
                </p>
                <footer style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: `rgba(255,255,255,0.85)`, marginTop: 8, letterSpacing: "0.15em" }}>— ROOD ZAPATA · CEO DELTA PROTEC INTEL</footer>
              </blockquote>

              {/* Misión · Visión · Historia tabs */}
              <div style={{ height: 1, background: `linear-gradient(90deg, transparent, rgba(107,0,21,0.4), rgba(200,120,20,0.3), transparent)` }} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {MISION_VISION.map((mv, i) => (
                  <div key={i} className="rounded-sm p-4 flex flex-col gap-2" style={{ background: "rgba(8,4,2,0.7)", border: `1px solid rgba(60,40,10,0.25)` }}>
                    <span style={{ fontFamily: "'Cinzel', serif", fontSize: "14px", color: "rgba(255,160,40,0.82)", letterSpacing: "0.15em" }}>{mv.num}</span>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "14px", color: `rgba(255,160,40,0.85)`, letterSpacing: "0.06em" }}>{mv.titulo}</p>
                    <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "12px", color: P.parchmentDim, lineHeight: 1.65 }}>{mv.txt}</p>
                  </div>
                ))}
              </div>

              {/* Valores */}
              <div style={{ height: 1, background: `linear-gradient(90deg, transparent, rgba(200,120,20,0.35), transparent)` }} />
              <p style={{ fontFamily: DT.terminal, fontSize: "14px", color: `rgba(255,160,40,0.7)`, letterSpacing: "0.25em" }}>MIS VALORES · LOS PILARES</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {VALORES.map((v, i) => (
                  <div key={i} className="rounded-sm p-3 flex flex-col gap-2" style={{ background: "rgba(8,4,2,0.6)", border: `1px solid rgba(60,40,10,0.2)` }}>
                    <CheckCircle size={12} color="rgba(155,0,255,0.7)" />
                    <p style={{ fontFamily: DT.decree, fontSize: "14px", color: P.parchment, letterSpacing: "0.05em" }}>{v.titulo}</p>
                    <p style={{ fontFamily: DT.notebook, fontSize: "12px", color: P.parchmentDim, lineHeight: 1.6 }}>{v.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="leyes"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="p-5 flex flex-col gap-3"
            >
              {LEYES.map((ley, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.08 * i }}
                  className="flex gap-3 p-3 rounded-sm"
                  style={{ background: "rgba(8,4,2,0.65)", border: `1px solid rgba(60,40,10,0.22)` }}
                >
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <span style={{ fontFamily: DT.greek, fontSize: "14px", color: "rgba(255,160,40,0.82)", letterSpacing: "0.05em", lineHeight: 1 }}>{ley.num}</span>
                    <div style={{ width: 1, flex: 1, background: `rgba(107,0,21,0.2)` }} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <p style={{ fontFamily: DT.decree, fontSize: "14px", color: `rgba(255,160,40,0.85)`, letterSpacing: "0.08em" }}>{ley.titulo}</p>
                    <p style={{ fontFamily: DT.notebook, fontSize: "12px", color: P.parchmentDim, lineHeight: 1.65 }}>{ley.txt}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

// ─── BLOQUE 8 · BÚSQUEDA DE FAMILIARES (con Relojito) ────────────────────────
function BloqueFamiliares() {
  const [tick, setTick] = useState(0);
  const [glitchCanal, setGlitchCanal] = useState(false);
  const [signalBars, setSignalBars] = useState([true, true, true, false]);
  const [scanPing, setScanPing] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const fire = () => { setGlitchCanal(true); setTimeout(() => setGlitchCanal(false), 180); };
    const iv = setInterval(fire, 3700);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const iv = setInterval(() => {
      const rnd = Math.random();
      if (rnd < 0.3) setSignalBars([true, true, true, false]);
      else if (rnd < 0.55) setSignalBars([true, true, true, true]);
      else if (rnd < 0.7) setSignalBars([true, true, false, false]);
      else setSignalBars([true, true, true, false]);
    }, 2200);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const iv = setInterval(() => setScanPing(p => p + 1), 2500);
    return () => clearInterval(iv);
  }, []);

  const hora = new Date().toLocaleTimeString("es-MX", { hour12: false });
  const fecha = new Date().toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric" }).toUpperCase();
  const bullets = [
    { icon: Shield, txt: "Operativo estrictamente confidencial en todas sus fases", delay: 0 },
    { icon: Eye,    txt: "Rastreo digital, geolocalización y análisis forense de grado militar", delay: 0.12 },
    { icon: Lock,   txt: "Verificación de datos 1:1 por videollamada cifrada antes de operar", delay: 0.24 },
    { icon: CheckCircle, txt: "Coordinación con autoridades cuando el caso lo amerita", delay: 0.36 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="relative rounded-sm overflow-hidden"
      style={{ background: "rgba(5,3,10,0.95)", border: `1px solid rgba(107,0,21,0.5)`, boxShadow: `0 0 60px rgba(50,0,8,0.5), inset 0 0 80px rgba(4,2,8,0.4)` }}
    >
      {/* ── Scan line barriendo ── */}
      <motion.div
        className="absolute left-0 right-0 pointer-events-none"
        style={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.6), rgba(255,160,40,0.25), rgba(107,0,21,0.6), transparent)", zIndex: 5 }}
        animate={{ top: ["-2%", "102%"] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "linear", repeatDelay: 3 }}
      />
      {/* ── Corner brackets ── */}
      {([
        { top: 0, left: 0 },
        { top: 0, right: 0 },
        { bottom: 0, right: 0 },
        { bottom: 0, left: 0 },
      ]).map((pos, i) => (
        <motion.div
          key={i}
          className="absolute pointer-events-none"
          style={{ ...pos, width: 18, height: 18, zIndex: 6 }}
          initial={{ opacity: 0, scale: 0.4 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 + i * 0.12, duration: 0.5 }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d={i === 0 ? "M2 10 L2 2 L10 2" : i === 1 ? "M8 2 L16 2 L16 10" : i === 2 ? "M16 8 L16 16 L8 16" : "M10 16 L2 16 L2 8"}
              stroke="rgba(107,0,21,0.75)" strokeWidth="1.5" strokeLinecap="square" />
          </svg>
        </motion.div>
      ))}
      {/* Imagen de fondo: brújula cósmica / constelaciones familiares */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `url(${compassImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        opacity: 0.2,
        mixBlendMode: "luminosity",
        filter: "saturate(0.2) brightness(0.7) contrast(1.1)",
      }} />
      {/* Velo sobre la imagen para mantener legibilidad */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "linear-gradient(135deg, rgba(5,2,10,0.72) 0%, rgba(3,1,6,0.45) 50%, rgba(5,2,10,0.78) 100%)",
      }} />

      <div style={{ height: 2, background: `linear-gradient(90deg, rgba(107,0,21,0.9), rgba(200,120,20,0.5), transparent)` }} />

      <div className="p-7 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">

        {/* ── Panel izquierdo con radar ── */}
        <div className="flex flex-col items-center gap-4">

          {/* Logo con anillos de radar pulsantes */}
          <div className="relative flex items-center justify-center" style={{ width: 130, height: 130 }}>
            {[0, 1, 2].map(i => (
              <motion.div
                key={`${scanPing}-${i}`}
                className="absolute rounded-full pointer-events-none"
                style={{ border: `1px solid rgba(107,0,21,${0.65 - i * 0.18})`, width: 96 + i * 28, height: 96 + i * 28 }}
                initial={{ opacity: 0.9, scale: 0.7 }}
                animate={{ opacity: 0, scale: 1.4 }}
                transition={{ duration: 2.2, delay: i * 0.45, ease: "easeOut" }}
              />
            ))}
            <motion.div
              animate={{ filter: [
                "drop-shadow(0 0 8px rgba(107,0,21,0.6))",
                "drop-shadow(0 0 28px rgba(255,160,40,0.85))",
                "drop-shadow(0 0 8px rgba(107,0,21,0.6))",
              ]}}
              transition={{ duration: 3.5, repeat: Infinity }}
            >
              <BotLogo size={92} />
            </motion.div>
          </div>

          <div className="text-center flex flex-col gap-1">
            <motion.p
              animate={{ textShadow: [`0 0 10px rgba(107,0,21,0.3)`, `0 0 30px rgba(107,0,21,0.8)`, `0 0 10px rgba(107,0,21,0.3)`] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,255,255,0.95)", letterSpacing: "0.12em", lineHeight: 1 }}
            >{hora}</motion.p>
            <p style={{ fontFamily: DT.data, fontSize: "11px", color: "rgba(255,160,40,0.7)", letterSpacing: "0.06em" }}>{fecha}</p>
          </div>

          {/* Barras de señal fluctuantes */}
          <div className="flex items-end gap-1">
            {signalBars.map((active, i) => (
              <motion.div
                key={i}
                animate={{ opacity: active ? [0.7, 1, 0.7] : 0.18 }}
                transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                style={{ width: 5, height: 6 + i * 4, background: active ? "rgba(80,200,80,0.85)" : "rgba(80,80,80,0.4)", borderRadius: 1 }}
              />
            ))}
            <span style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(80,200,80,0.7)", letterSpacing: "0.1em", marginLeft: 5 }}>
              {signalBars.filter(Boolean).length}/4
            </span>
          </div>

          <div style={{ width: "100%", height: 1, background: `linear-gradient(90deg, transparent, rgba(107,0,21,0.5), transparent)` }} />

          <div className="flex flex-col gap-2 w-full">
            {/* OPERATIVO — parpadeo verde */}
            <div className="flex justify-between items-center">
              <span style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(255,160,40,0.85)", letterSpacing: "0.1em" }}>OPERATIVO</span>
              <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.1, repeat: Infinity }}
                style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(80,200,80,0.9)", letterSpacing: "0.05em" }}>● ACTIVO</motion.span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(255,160,40,0.85)", letterSpacing: "0.1em" }}>PROTOCOLO</span>
              <span style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,160,40,0.9)" }}>VERIFICACIÓN</span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(255,160,40,0.85)", letterSpacing: "0.1em" }}>COBERTURA</span>
              <span style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(255,255,255,0.75)" }}>NACIONAL</span>
            </div>
            {/* CANAL — glitch aleatorio */}
            <div className="flex justify-between items-center">
              <span style={{ fontFamily: DT.data, fontSize: "14px", color: "rgba(255,160,40,0.85)", letterSpacing: "0.1em" }}>CANAL</span>
              <motion.span
                animate={glitchCanal ? { x: [-2, 2, -1, 0], skewX: [-5, 5, -2, 0] } : undefined}
                style={{ fontFamily: DT.terminal, fontSize: "14px", color: "rgba(180,20,40,0.9)", letterSpacing: "0.05em" }}
              >{glitchCanal ? "█NKR1PT▓DO" : "ENCRIPTADO"}</motion.span>
            </div>
          </div>

          {/* Barra de progreso de rastreo */}
          <div className="w-full flex flex-col gap-1">
            <div className="flex justify-between">
              <span style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.85)", letterSpacing: "0.1em" }}>RASTREO</span>
              <span style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(255,160,40,0.6)" }}>EN CURSO</span>
            </div>
            <div style={{ width: "100%", height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
              <motion.div
                style={{ height: "100%", background: "linear-gradient(90deg, rgba(107,0,21,0.8), rgba(180,30,50,0.9))", borderRadius: 2 }}
                animate={{ width: ["30%", "85%", "60%", "95%", "45%"] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </div>
        </div>

        {/* ── Contenido central ── */}
        <div className="md:col-span-2 flex flex-col gap-5">

          <div className="flex items-start gap-3">
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5], rotate: [0, 5, 0, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <Search size={22} color="rgba(107,0,21,0.9)" />
            </motion.div>
            <div className="flex flex-col gap-0.5">
              <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "11px", color: "rgba(255,160,40,0.85)", letterSpacing: "0.2em" }}>
                BLOQUE VIII · UNIDAD ESPECIALIZADA
              </p>
              <motion.h4
                animate={{ textShadow: [
                  "0 0 8px rgba(107,0,21,0.3)",
                  "0 0 22px rgba(107,0,21,0.7), 0 0 40px rgba(80,0,10,0.3)",
                  "0 0 8px rgba(107,0,21,0.3)",
                ]}}
                transition={{ duration: 3.5, repeat: Infinity }}
                style={{ fontFamily: "'Cinzel', serif", fontSize: "16px", color: "rgba(255,255,255,0.97)", letterSpacing: "0.12em" }}
              >
                BÚSQUEDA DE FAMILIARES
              </motion.h4>
            </div>
          </div>

          <motion.div
            style={{ height: 1, background: `linear-gradient(90deg, rgba(107,0,21,0.7), rgba(200,120,20,0.4), transparent)` }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 4, repeat: Infinity }}
          />

          <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "13px", color: "rgba(255,255,255,0.88)", lineHeight: 1.7, letterSpacing: "0.04em" }}>
            Donde otros ven un callejón sin salida, nosotros vemos un rastro. Ya sea por conflictos internos o desapariciones, nuestra unidad especializada cierra la brecha entre la ausencia y el hallazgo.
            <motion.span
              animate={{ textShadow: ["0 0 6px rgba(255,160,40,0.2)", "0 0 18px rgba(255,160,40,0.7)", "0 0 6px rgba(255,160,40,0.2)"] }}
              transition={{ duration: 3, repeat: Infinity }}
              style={{ color: "rgba(255,160,40,0.97)" }}
            > Localizamos y reconectamos con precisión quirúrgica</motion.span>, devolviendo la claridad a situaciones donde el silencio parece haber ganado.
            Si hay un rastro, lo encontraremos.
          </p>

          {/* Bullets con entrada animada y hover */}
          <div className="flex flex-col gap-2">
            {bullets.map((item, i) => (
              <motion.div
                key={i}
                className="flex items-center gap-3"
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: item.delay, duration: 0.5 }}
                whileHover={{ x: 4 }}
              >
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.3, 1] }}
                  transition={{ duration: 2 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}
                >
                  <item.icon size={11} color="rgba(107,0,21,0.9)" style={{ flexShrink: 0 }} />
                </motion.div>
                <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "13px", color: "rgba(255,255,255,0.88)", letterSpacing: "0.04em" }}>{item.txt}</span>
              </motion.div>
            ))}
          </div>

          {/* REGLA DE ORO con glow pulsante */}
          <motion.div
            className="rounded-sm p-4"
            style={{ background: "rgba(8,4,2,0.75)", border: `1px solid rgba(107,0,21,0.35)`, borderLeft: `3px solid rgba(107,0,21,0.8)` }}
            animate={{ boxShadow: [
              "inset 0 0 0px rgba(107,0,21,0)",
              "inset 0 0 22px rgba(107,0,21,0.2)",
              "inset 0 0 0px rgba(107,0,21,0)",
            ]}}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <motion.p
              animate={{ textShadow: ["0 0 4px rgba(255,160,40,0.2)", "0 0 14px rgba(255,160,40,0.8)", "0 0 4px rgba(255,160,40,0.2)"] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: "rgba(255,160,40,0.97)", letterSpacing: "0.12em", marginBottom: 6 }}
            >
              ⚠ REGLA DE ORO
            </motion.p>
            <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "13px", color: "rgba(255,255,255,0.88)", lineHeight: 1.7, letterSpacing: "0.04em" }}>
              Antes de cualquier operación se realiza una <strong style={{ color: "rgba(255,160,40,0.97)" }}>verificación de datos 1:1</strong> por canales encriptados — videollamada obligatoria. Sin verificación, no hay operativo. La discreción y la certeza son innegociables.
            </p>
          </motion.div>

          {/* Botón con shimmer + latencia */}
          <div className="flex items-center gap-4">
            <motion.a
              href="https://t.me/Unit_Intel_Delta_Bot"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, boxShadow: `0 0 40px rgba(107,0,21,0.8)` }}
              whileTap={{ scale: 0.97 }}
              className="self-start flex items-center gap-3 px-7 py-3 rounded-sm"
              style={{ background: `linear-gradient(135deg, rgba(107,0,21,0.85), rgba(60,0,8,0.98))`, border: `1px solid rgba(160,20,40,0.5)`, textDecoration: "none", fontFamily: "'Cinzel', serif", fontSize: "14px", color: "rgba(255,255,255,0.97)", letterSpacing: "0.15em", position: "relative", overflow: "hidden" }}
            >
              <motion.div
                style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)", pointerEvents: "none" }}
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 3 }}
              />
              <motion.div animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 4 }}>
                <Send size={12} />
              </motion.div>
              INICIAR BÚSQUEDA
            </motion.a>
            <div className="flex flex-col gap-0.5">
              <span style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(80,200,80,0.7)", letterSpacing: "0.1em" }}>LATENCIA</span>
              <motion.span
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 1.8, repeat: Infinity }}
                style={{ fontFamily: DT.terminal, fontSize: "10px", color: "rgba(80,200,80,0.9)" }}
              >
                {12 + (tick % 7)}ms ▲
              </motion.span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── BLOQUE 9 · BYPASS & RECUPERACIÓN ────────────────────────────────────────
const BYPASS_CARDS = [
  {
    icon: LockOpen,
    titulo: "MÓDULO iOS (BYPASS)",
    desc: "SALTO DE ACTIVACIÓN Y LIMPIEZA DE REGISTROS PARA DISPOSITIVOS BLOQUEADOS POR SISTEMA. RECUPERACIÓN TOTAL DE FUNCIONES.",
    tag: "iOS · iPadOS",
    delay: 0,
  },
  {
    icon: Cpu,
    titulo: "TERMINALES POS (BANCARIAS)",
    desc: "REINSTALACIÓN DE SISTEMA Y LIBERACIÓN DE TERMINALES DE PUNTO DE VENTA. OPTIMIZACIÓN DE SOFTWARE PARA USO LIBRE.",
    tag: "POS · BANCARIO",
    delay: 0.14,
  },
  {
    icon: Wifi,
    titulo: "GESTIÓN DE CONECTIVIDAD (IMEI)",
    desc: "DIAGNÓSTICO PROFUNDO Y RETIRO DE REPORTES EN BASE DE DATOS. RECUPERACIÓN DE SEÑAL Y ENLACE DE RED.",
    tag: "IMEI · RED",
    delay: 0.28,
  },
];

function BloqueBypass() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.85 }}
      className="relative rounded-sm overflow-hidden"
      style={{
        background: "rgba(5,3,10,0.97)",
        border: "1px solid rgba(107,0,21,0.55)",
        boxShadow: "0 0 70px rgba(50,0,8,0.55), inset 0 0 90px rgba(4,2,8,0.5)",
      }}
    >
      {/* Top accent bar */}
      <div style={{ height: 2, background: "linear-gradient(90deg, rgba(107,0,21,0.9), rgba(200,120,20,0.6), rgba(107,0,21,0.9))" }} />

      {/* Scan line */}
      <motion.div
        className="absolute left-0 right-0 pointer-events-none"
        style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.55), rgba(255,160,40,0.2), rgba(107,0,21,0.55), transparent)", zIndex: 5 }}
        animate={{ top: ["-1%", "101%"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear", repeatDelay: 4 }}
      />

      {/* Corner brackets */}
      {[{ top: 0, left: 0 }, { top: 0, right: 0 }, { bottom: 0, right: 0 }, { bottom: 0, left: 0 }].map((pos, i) => (
        <motion.div
          key={i}
          className="absolute pointer-events-none"
          style={{ ...pos, width: 18, height: 18, zIndex: 6 }}
          initial={{ opacity: 0, scale: 0.4 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 + i * 0.12, duration: 0.5 }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d={i === 0 ? "M2 10 L2 2 L10 2" : i === 1 ? "M8 2 L16 2 L16 10" : i === 2 ? "M16 8 L16 16 L8 16" : "M10 16 L2 16 L2 8"}
              stroke="rgba(107,0,21,0.8)" strokeWidth="1.5" strokeLinecap="square"
            />
          </svg>
        </motion.div>
      ))}

      <div className="relative z-10 flex flex-col gap-5 p-6">

        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <motion.div
              style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(80,220,80,0.85)", boxShadow: "0 0 8px rgba(80,220,80,0.9)", flexShrink: 0 }}
              animate={{ opacity: [1, 0.3, 1], scale: [1, 1.5, 1] }}
              transition={{ duration: 1.6, repeat: Infinity }}
            />
            <motion.span
              animate={{ opacity: [0.75, 1, 0.75] }}
              transition={{ duration: 2.2, repeat: Infinity }}
              style={{ fontFamily: DT.terminal, fontSize: "9px", color: "rgba(80,220,80,0.9)", letterSpacing: "0.26em", textShadow: "0 0 10px rgba(80,220,80,0.6)" }}
            >
              STATUS: SYSTEM_READY // BYPASS_ENABLED
            </motion.span>
          </div>

          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <LockOpen size={20} color="rgba(255,160,40,0.95)" style={{ filter: "drop-shadow(0 0 8px rgba(255,160,40,0.7))" }} />
            </motion.div>
            <motion.h2
              animate={{ textShadow: ["0 0 18px rgba(107,0,21,0.45), 0 0 36px rgba(80,0,10,0.2)", "0 0 36px rgba(107,0,21,0.85), 0 0 72px rgba(60,0,8,0.45)", "0 0 18px rgba(107,0,21,0.45), 0 0 36px rgba(80,0,10,0.2)"] }}
              transition={{ duration: 4.5, repeat: Infinity }}
              style={{ fontFamily: DT.greek, fontSize: "17px", color: P.parchment, letterSpacing: "0.12em", textTransform: "uppercase" }}
            >
              ΠΑΡΑΚΑΜΨΗ: PROTOCOLO BYPASS Y RECUPERACIÓN
            </motion.h2>
          </div>

          <div style={{ width: "100%", height: 1, background: "linear-gradient(90deg, rgba(107,0,21,0.7), rgba(200,120,20,0.45), transparent)" }} />
        </div>

        {/* Body: visual panel + text */}
        <div className="flex flex-col md:flex-row gap-6 items-stretch">

          {/* Thumbnail — imagen real del protocolo Bypass */}
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative flex-shrink-0 rounded-sm overflow-hidden"
            style={{
              width: "clamp(220px, 38%, 320px)",
              border: "1px solid rgba(107,0,21,0.65)",
              boxShadow: "0 0 50px rgba(40,0,8,0.85), 0 0 20px rgba(107,0,21,0.3)",
            }}
          >
            {/* Pulsing crimson glow overlay */}
            <motion.div
              className="absolute inset-0 pointer-events-none rounded-sm"
              animate={{ boxShadow: ["inset 0 0 0px rgba(107,0,21,0)", "inset 0 0 40px rgba(107,0,21,0.35)", "inset 0 0 0px rgba(107,0,21,0)"] }}
              transition={{ duration: 3.5, repeat: Infinity }}
              style={{ zIndex: 3 }}
            />
            {/* Scan line */}
            <motion.div
              className="absolute left-0 right-0 pointer-events-none"
              style={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.55), rgba(255,160,40,0.2), rgba(107,0,21,0.55), transparent)", zIndex: 4 }}
              animate={{ top: ["-2%", "102%"] }}
              transition={{ duration: 3.8, repeat: Infinity, ease: "linear", repeatDelay: 2.2 }}
            />
            <img
              src={bypassThumb}
              alt="ΠΡΩΤΟΚΟΛΛΟ ΠΑΡΑΚΑΜΨΗΣ — BYPASS PROTOCOL"
              style={{ width: "100%", display: "block", objectFit: "cover" }}
            />
            {/* Bottom label strip */}
            <div
              className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-1"
              style={{ background: "rgba(4,2,8,0.82)", borderTop: "1px solid rgba(107,0,21,0.45)", zIndex: 5 }}
            >
              <motion.span
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{ fontFamily: DT.terminal, fontSize: "8px", color: "rgba(255,160,40,0.9)", letterSpacing: "0.22em" }}
              >
                ΞΕΚΛΕΙΔΩΜΑ · ΣΥΣΚΕΥΩΝ
              </motion.span>
            </div>
          </motion.div>

          {/* Body text — centrado verticalmente */}
          <div className="flex flex-col justify-center flex-1">
            <p style={{ fontFamily: DT.notebook, fontSize: "14px", color: "#ffffff", lineHeight: 1.9, letterSpacing: "0.03em", textTransform: "uppercase", textShadow: "0 1px 4px rgba(0,0,0,0.98)" }}>
              SI EL HARDWARE ES TUYO, EL SOFTWARE NO TIENE POR QUÉ SER DE ALGUIEN MÁS. EN LA UNIDAD DELTA NO ACEPTAMOS{" "}
              <span style={{ color: "rgba(210,70,70,0.95)", fontStyle: "italic" }}>'PISAPAPELES' DE LUJO.</span>{" "}
              SI TE VENDIERON UN EQUIPO CON CUENTA BLOQUEADA, IMEI RESTRINGIDO O UNA TERMINAL BANCARIA INSERVIBLE,{" "}
              <span style={{ color: "rgba(255,160,40,0.97)", fontStyle: "italic" }}>FORZAMOS LA ENTRADA Y DEVOLVEMOS LA VIDA AL DISPOSITIVO.</span>{" "}
              NO PEDIMOS PERMISO AL SISTEMA, SIMPLEMENTE IGNORAMOS SUS REGLAS.
            </p>
          </div>

        </div>

        {/* Separator */}
        <div className="flex items-center gap-3">
          <motion.div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.6))" }} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2.8, repeat: Infinity }} />
          <motion.span animate={{ opacity: [0.4, 1, 0.4], rotate: [0, 180, 360] }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }} style={{ color: "rgba(255,160,40,0.8)", fontSize: "10px" }}>✟</motion.span>
          <motion.div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(107,0,21,0.6), transparent)" }} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2.8, repeat: Infinity, delay: 0.6 }} />
        </div>

        {/* Capability cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {BYPASS_CARDS.map(({ icon: Icon, titulo, desc, tag, delay }, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay, duration: 0.6 }}
              whileHover={{ y: -4, boxShadow: "0 0 36px rgba(107,0,21,0.4), 0 0 14px rgba(200,120,20,0.2)" }}
              className="relative rounded-sm p-4 flex flex-col gap-3 cursor-default overflow-hidden"
              style={{ background: "rgba(10,4,2,0.75)", border: "1px solid rgba(107,0,21,0.3)" }}
            >
              <motion.div
                style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, transparent 0%, rgba(107,0,21,0.07) 50%, transparent 100%)", pointerEvents: "none" }}
                animate={{ opacity: [0, 0.8, 0] }}
                transition={{ duration: 3 + i * 0.5, repeat: Infinity, delay: i * 1.2 }}
              />
              <div className="flex items-center justify-between relative z-10">
                <motion.div
                  animate={{ filter: ["drop-shadow(0 0 4px rgba(255,160,40,0.4))", "drop-shadow(0 0 12px rgba(255,160,40,0.9))", "drop-shadow(0 0 4px rgba(255,160,40,0.4))"] }}
                  transition={{ duration: 2.5 + i * 0.4, repeat: Infinity }}
                >
                  <Icon size={18} color="rgba(255,160,40,0.9)" />
                </motion.div>
                <span style={{ fontFamily: DT.terminal, fontSize: "8px", color: "rgba(200,150,40,0.95)", letterSpacing: "0.18em", background: "rgba(107,0,21,0.14)", border: "1px solid rgba(107,0,21,0.38)", padding: "2px 6px", borderRadius: 2, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                  {tag}
                </span>
              </div>
              <motion.p
                animate={{ textShadow: ["0 0 6px rgba(255,160,40,0.2)", "0 0 18px rgba(255,160,40,0.75)", "0 0 6px rgba(255,160,40,0.2)"] }}
                transition={{ duration: 3 + i * 0.5, repeat: Infinity }}
                style={{ fontFamily: DT.decree, fontSize: "13px", color: "rgba(255,160,40,0.95)", letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: 1.35, position: "relative", zIndex: 10, textShadow: "0 1px 4px rgba(0,0,0,1)" }}
              >
                {titulo}
              </motion.p>
              <p style={{ fontFamily: DT.notebook, fontSize: "12px", color: P.parchmentDim, lineHeight: 1.65, letterSpacing: "0.03em", position: "relative", zIndex: 10 }}>
                {desc}
              </p>
              <motion.div
                style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, transparent, rgba(107,0,21,0.7), transparent)" }}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 2.2 + i * 0.4, repeat: Infinity, delay: i * 0.5 }}
              />
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-center mt-1">
          <motion.a
            href="https://wa.me/deltaprotecintelmx"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.04, boxShadow: "0 0 50px rgba(107,0,21,0.9), 0 0 24px rgba(200,120,20,0.3)" }}
            whileTap={{ scale: 0.97 }}
            className="relative flex items-center gap-3 px-10 py-3 rounded-sm overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(107,0,21,0.88), rgba(60,0,8,0.98))", border: "1px solid rgba(160,20,40,0.55)", textDecoration: "none", fontFamily: DT.decree, fontSize: "14px", color: P.parchment, letterSpacing: "0.2em", cursor: "pointer" }}
          >
            <motion.div
              style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)", pointerEvents: "none" }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 3 }}
            />
            <motion.div
              animate={{ rotate: [0, -15, 15, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
            >
              <LockOpen size={14} />
            </motion.div>
            SOLICITAR BYPASS INMEDIATO
          </motion.a>
        </div>

      </div>

      {/* Bottom accent bar */}
      <div style={{ height: 2, background: "linear-gradient(90deg, rgba(107,0,21,0.9), rgba(200,120,20,0.6), rgba(107,0,21,0.9))" }} />
    </motion.div>
  );
}

// ─── BOTÓN CONTACTO FINAL ─────────────────────────────────────────────────────
function ContactoFinal() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="relative rounded-sm overflow-hidden p-7 flex flex-col items-center gap-4 text-center"
      style={{ background: "rgba(5,2,10,0.97)", border: `1px solid rgba(107,0,21,0.5)`, boxShadow: `0 0 60px rgba(60,0,8,0.45)` }}
    >
      <motion.div
        animate={{ opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 50% 50%, rgba(107,0,21,0.15), transparent 65%)` }}
      />
      <div className="relative z-10 flex flex-col items-center gap-3">
        <p style={{ fontFamily: DT.decree, fontSize: "17px", color: P.parchment, letterSpacing: "0.12em" }}>
          ¿LISTO PARA ACTUAR?
        </p>
        <p style={{ fontFamily: DT.notebook, fontSize: "14px", color: P.parchmentDim, lineHeight: 1.65, maxWidth: 480, letterSpacing: "0.04em" }}>
          El primer paso es tuyo. Contacta directamente al equipo Delta a través de los canales oficiales y encriptados.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          {[
            { icon: Send, label: "TELEGRAM BOT",     href: "https://t.me/Unit_Intel_Delta_Bot",        color: MP.crimsonBright },
            { icon: Phone, label: "WHATSAPP",         href: "https://wa.me/deltaprotecintelmx",          color: "rgba(255,160,40,0.9)" },
            { icon: Mail,  label: "CORREO CIFRADO",   href: "mailto:roodzapata@deltaprotec1ntelmc.cymru", color: MP.crimsonBright },
          ].map(c => (
            <motion.a
              key={c.label}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, boxShadow: `0 0 24px ${c.color}` }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2 px-6 py-3 rounded-sm"
              style={{ background: `${c.color}`, border: `1px solid ${c.color}`, textDecoration: "none", fontFamily: DT.terminal, fontSize: "13px", color: P.parchment, letterSpacing: "0.15em" }}
            >
              <c.icon size={13} />
              {c.label}
            </motion.a>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export function UnidadInteligencia() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ fontFamily: DT.greek, color: P.parchment, background: MP.abyss }}
    >
      <MysticalBg variant="intel" />

      {/* Botón de retroceso */}
      <BackButton />

      {/* Esquinas decorativas */}
      {[
        { pos: { top: 0, left: 0 },      b: { borderTop: `1px solid rgba(107,0,21,0.6)`, borderLeft: `1px solid rgba(107,0,21,0.6)` } },
        { pos: { top: 0, right: 0 },     b: { borderTop: `1px solid rgba(200,120,20,0.5)`, borderRight: `1px solid rgba(200,120,20,0.5)` } },
        { pos: { bottom: 0, left: 0 },   b: { borderBottom: `1px solid rgba(200,120,20,0.5)`, borderLeft: `1px solid rgba(200,120,20,0.5)` } },
        { pos: { bottom: 0, right: 0 },  b: { borderBottom: `1px solid rgba(107,0,21,0.6)`, borderRight: `1px solid rgba(107,0,21,0.6)` } },
      ].map((c, i) => (
        <div key={i} className="absolute w-12 h-12 pointer-events-none" style={{ ...c.pos, ...c.b, zIndex: 2 }} />
      ))}

      <SiteHeader />

      <main className="relative flex-1 z-10 flex flex-col gap-10 px-4 py-8 max-w-[1440px] mx-auto w-full">

        {/* Encabezado de sección */}
        <motion.div
          initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
          className="text-center flex flex-col items-center gap-2"
        >
          <motion.h1
            animate={{ textShadow: [`0 0 30px rgba(107,0,21,0.45)`, `0 0 60px rgba(107,0,21,0.75)`, `0 0 30px rgba(107,0,21,0.45)`] }}
            transition={{ duration: 5, repeat: Infinity }}
            style={{ fontFamily: DT.greek, fontSize: "18px", color: P.parchment, letterSpacing: "0.1em" }}
          >
            Δ UNIDAD DE INTELIGENCIA Δ
          </motion.h1>
          <div style={{ width: "clamp(320px, 60%, 720px)", height: 1, background: `linear-gradient(90deg, transparent, rgba(107,0,21,0.55), rgba(200,120,20,0.45), rgba(107,0,21,0.55), transparent)` }} />
          <p style={{ fontFamily: DT.terminal, fontSize: "11px", color: P.parchmentDim, letterSpacing: "0.28em" }}>
            OMNISCIENT OBSERVER · GLOBAL JUSTICE · SIEMPRE VIGILANTES
          </p>
        </motion.div>

        {/* ① ZONA SEGURA */}
        <section>
          <SectionTitle greek="I. ZONA SEGURA" spanish="APOYO DE EMERGENCIA · GRATUITO" />
          <div className="mt-3">
            <ZonaSegura />
          </div>
        </section>

        {/* ② CARRUSEL DE REFERENCIAS */}
        <section className="flex flex-col gap-4">
          <SectionTitle greek="II. REFERENCIAS" spanish="TESTIMONIOS VERIFICADOS · RED SOCIAL" />
          <CarruselReferencias />
        </section>

        {/* ③ BIBLIOTECA DE MEDIOS */}
        <section className="flex flex-col gap-3">
          <SectionTitle greek="III. BIBLIOTECA DE INTELIGENCIA" spanish="MANUALES EDUCATIVOS · DESCARGA LIBRE" />
          <p style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "13px", color: P.parchmentDim, lineHeight: 1.7, letterSpacing: "0.04em", textAlign: "center", maxWidth: 900, margin: "0 auto" }}>
            La prevención es tu primera línea de defensa. Aquí tienes acceso directo a nuestra base de inteligencia para blindar tu entorno. El conocimiento está disponible y listo para su consulta. No esperes a que sea tarde.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {DOCS.map((doc, i) => <DocCard key={doc.id} doc={doc} idx={i} />)}
          </div>
        </section>

        {/* ④ PRODUCTOS */}
        <section className="flex flex-col gap-3">
          <SectionTitle greek="IV. NUESTROS PROTOCOLOS" spanish="SERVICIOS OPERATIVOS · DELTA PROTEC" />
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            {PRODUCTOS.map((prod, i) => <ProductoCard key={prod.num} prod={prod} idx={i} />)}
          </div>
        </section>

        {/* ⑤ FINANZAS */}
        <section className="flex flex-col gap-4">
          <SectionTitle greek="V. ASESORÍA FINANCIERA" spanish="ESTRATEGIA DE SALIDA · MONTADEUDAS" />
          <BloqueFinanzas />
        </section>

        {/* ⑥ MASTERCLASS */}
        <section className="flex flex-col gap-4">
          <SectionTitle greek="VI. MASTERCLASS" spanish="ACOMPAÑAMIENTO INDIVIDUAL · 5 SEMANAS" />
          <BloqueMasterclass />
        </section>

        {/* ⑦ LEY DE HIERRO */}
        <section className="flex flex-col gap-4">
          <SectionTitle greek="VII. LEY DE HIERRO" spanish="MISIÓN · VISIÓN · HISTORIA · VALORES" />
          <BloqueLeyDeHierro />
        </section>

        {/* ⑧ BÚSQUEDA DE FAMILIARES */}
        <section className="flex flex-col gap-4">
          <SectionTitle greek="VIII. BÚSQUEDA DE FAMILIARES" spanish="RASTREO ESPECIALIZADO · PROTOCOLO ΔΕΛΤΑ" />
          <BloqueFamiliares />
        </section>

        {/* ⑨ BYPASS & RECUPERACIÓN */}
        <section className="flex flex-col gap-4">
          <SectionTitle greek="IX. ΠΑΡΑΚΑΜΨΗ" spanish="PROTOCOLO BYPASS Y RECUPERACIÓN · GRADO MILITAR" />
          <BloqueBypass />
        </section>

        {/* BOTÓN CONTACTO FINAL */}
        <ContactoFinal />

      </main>

      <SiteFooter />
    </div>
  );
}
